import { keepUnsavedPhotoBubbles } from '../utils/annotatedImages';
import { newMessageId } from '../utils/messageId';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import OrderAttentionStrip from '../components/features/orders/OrderAttentionStrip';
import SkippedLinesBar from '../components/features/chat/SkippedLinesBar';
import { stageOf } from '../components/features/orders/orderLifecycle';
import { useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';
import ChatService from '../services/ChatService';
import FeedbackService from '../services/FeedbackService';
import { orderService, OrderResponse } from '../services/OrderService';
import type { ConversationMessage } from '../services/OrganizationService';
import AppLayout from '../layout/AppLayout';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { firebaseObject } from '../config/firebase';
import SessionRatingModal from '../components/features/chat/SessionRatingModal';
import AssistanceRequestModal from '../components/common/modals/AssistanceRequestModal';
import OrdersPanelStatic from '../layout/SidePanelStatic';
import {
  Message,
  MessageList,
  ChatInput,
  ContextTagsBar,
  CartSidePanel,
  ChatHeader,
  PartResult,
  PartsTrackerPanel,
} from '../components/features/chat';
import { ShoppingCart, X } from 'lucide-react';
import { cartOrderService } from '../services/CartOrderService';
import { repairEstimationService } from '../services/RepairEstimationService';
import { useSession } from '../contexts/SessionContext';
import { useOrder } from '../contexts/OrderContext';
import { useAuth } from '../contexts/AuthContext';
import { usePartSelectionMode } from '../contexts/PartSelectionModeContext';
import { useQuota } from '../contexts/QuotaContext';
import MobileHeader from '../layout/MobileHeader';
import ConfirmationModal from '../components/common/modals/ConfirmationModal';
import ConfirmDialog from '../components/common/modals/ConfirmDialog';
import { LockedOrderContext } from '../components/features/chat/lockedOrder';
import { generateIntentPreview, INTENT_PROMPTS } from '../services/IntentPreviewService';
import { useStreamSend } from '../hooks/useStreamSend';
import { useTrackedParts } from '../hooks/useTrackedParts';
import { useClickTrail } from '../hooks/useClickTrail';
import { parseCatalogQuantity } from '../utils/quantity';
import { transcribeAudio } from '../services/TranscriptionService';

/**
 * Collapse user messages that were re-persisted by backend 429/503 retries.
 *
 * When an LLM call hits a rate-limit (429) or unavailable (503) error, the
 * backend re-runs the whole turn; each re-run re-appends the SAME user message
 * to the session with a fresh id, so a single user turn shows up as several
 * identical user bubbles (40–60s apart). The retries themselves are fine — we
 * just hide their duplicate user messages on the client.
 *
 * Heuristic: a user message whose trimmed text matches the most recent user
 * message is treated as a retry and dropped. Bot messages in between (the failed
 * attempts' intermediate echoes) do NOT reset the comparison, so a run of
 * "user → (retry echoes) → user → …" collapses to the first bubble. A genuinely
 * different user turn resets the baseline, so a later legitimate re-ask of the
 * same text (after a different message) is preserved.
 */
let _uploadSeq = 0;
function newUploadId(): string {
  _uploadSeq += 1;
  return `up_${Date.now().toString(36)}_${_uploadSeq}`;
}

function dropRetryDuplicateUserMessages(messages: Message[]): Message[] {
  let lastUserText: string | null = null;
  const out: Message[] = [];
  for (const m of messages) {
    if (m.sender === 'user') {
      const norm = (m.text || '').trim();
      if (norm && norm === lastUserText) {
        // Identical to the previous user submission → a 429/503 retry duplicate.
        continue;
      }
      lastUserText = norm;
    }
    out.push(m);
  }
  return out;
}

/** Hide the user turn that answered a clarification form on history reload.
 *  The answered clarification bot bubble already shows the picks + "Answer
 *  submitted", so echoing the answer as a separate user bubble is redundant
 *  (matches the live behaviour, which flags the answer `hiddenFromChat`).
 *  Detected by the immediately-preceding bot message carrying BOTH clarification
 *  questions and merged answers in its envelope text. */
function markHiddenClarificationAnswers(messages: Message[]): Message[] {
  const answeredClarification = (m?: Message): boolean => {
    if (!m || m.sender !== 'bot') return false;
    const t = (m.text || '').trim();
    if (!t.startsWith('{')) return false;
    try {
      const o = JSON.parse(t);
      return Array.isArray(o?.clarification_questions)
        && o.clarification_questions.length > 0
        && o?.clarification_answers != null;
    } catch {
      return false;
    }
  };
  return messages.map((m, i) =>
    m.sender === 'user' && !m.hiddenFromChat && answeredClarification(messages[i - 1])
      ? { ...m, hiddenFromChat: true }
      : m,
  );
}

/** The composer must not compete with a pending diagram question. Free-form
 * text would bypass the structured catalog selection and can restart search. */
function hasPendingDiagramChoice(messages: Message[]): boolean {
  const last = [...messages].reverse().find(m => !m.hiddenFromChat);
  if (!last || last.sender !== 'bot') return false;
  try {
    const envelope = JSON.parse((last.text || '').trim());
    if (envelope?.clarification_answers != null) return false;
    const questions = envelope?.clarification_questions;
    return Array.isArray(questions) && questions.some((q: any) =>
      q?.question_type !== 'free_text' && Array.isArray(q?.options) &&
      q.options.some((o: any) => Array.isArray(o?.load) &&
        o.load.some((r: any) => Boolean(r?.group_id && r?.section_id)))
    );
  } catch {
    return false;
  }
}

const ChatPage: React.FC = () => {
  // Session is now the primary entity
  const {
    activeSessionId,
    setActiveSessionId,
    activeAbortControllerRef,
  } = useSession();
  const { currentUser } = useAuth();
  const { partSelectionMode } = usePartSelectionMode();
  const { paused: quotaPausedMetrics } = useQuota();
  const { setActiveOrderId, removeActiveOrderId, getActiveOrderId, setOrderObject, getOrderObject } = useOrder();
  // Record every click on this order so support can replay the session.
  useClickTrail(getActiveOrderId(), activeSessionId);
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();


  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  // Get cart items from associated order or fallback to empty array
  const cartItems = getOrderObject()?.parts_in_order?.items || [];
  const totalItems = cartItems.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0);

  // Declare all state variables first
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());
  const [contextTags, setContextTags] = useState<string[]>([]);
  const [userTags, setUserTags] = useState<string[]>([]);
  const [backendTags, setBackendTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [showAddTag, setShowAddTag] = useState(false);
  const [showSessionRating, setShowSessionRating] = useState(false);
  const [sessionRating, setSessionRating] = useState(0);
  const [sessionComments, setSessionComments] = useState('');
  const [sessionRatingSubmitted, setSessionRatingSubmitted] = useState(false);
  const [hasSubmittedRating, setHasSubmittedRating] = useState(false);
  const [showMobileOrders, setShowMobileOrders] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [hasProcessedVin, setHasProcessedVin] = useState(false);
  const [isProcessingVin, setIsProcessingVin] = useState(false);
  const [vinAutoSendTrigger, setVinAutoSendTrigger] = useState(0); // Trigger to force VIN auto-send check
  const [hasDraftOrders, setHasDraftOrders] = useState(false);
  const [summaryItems, setSummaryItems] = useState<string[]>([]);
  // Keeps the latest summaryItems accessible inside stable useCallback closures without
  // causing them to be recreated on every render.
  const summaryItemsRef = useRef<string[]>([]);
  useEffect(() => { summaryItemsRef.current = summaryItems; }, [summaryItems]);

  // ── Floating parts tracker ──────────────────────────────────────────────
  // Single source of truth for the session's parts list. The backend's
  // ValidationAgentResponse.parts is cumulative across the session, so each new
  // validation simply replaces the whole list (latest wins — on history reload
  // bubbles mount in order, so the last validation ends up here too).
  // Requested-parts tracker rows, derived from the conversation. Shared with the
  // guest chat via useTrackedParts so both pages build identical rows (and both
  // get the floating panel rather than BotMessage's legacy in-bubble list).
  const {
    trackedParts,
    trackedServiceTitle,
    resetTrackedParts,
    handleValidationParts,
    trackerFoundParts,
    trackerAnnotatedImages,
    trackerVisionPartImages,
    trackerNotFoundParts,
    trackerLines,
  } = useTrackedParts(messages, activeSessionId);

  // Pending hidden annotations to be injected into the NEXT user message.
  // Keyed by original item text so multiple edits to the same item are collapsed
  // into a single "update" annotation (last-write-wins).  Delete annotations are
  // stored with a special sentinel key ('__delete__:<item>') so they don't clash.
  const pendingSummaryAnnotationsRef = useRef<Map<string, string>>(new Map());

  // Increment to force a re-fetch of session history (e.g. after background Phase-2 completes)
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [showAssistanceModal, setShowAssistanceModal] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  // True when the server is still processing (action loop running) after a client disconnect.
  // While this is true we poll streaming_status and reload history once it finishes.
  const [isBackgroundProcessing, setIsBackgroundProcessing] = useState(false);
  const backgroundPollingRef = useRef<NodeJS.Timeout | null>(null);
  // Whether the desktop cart panel is minimized — lifted from CartSidePanel so
  // the chat composer can reserve the matching right-side width.
  const [cartMinimized, setCartMinimized] = useState(false);

  // Cancel background polling whenever the active session changes
  useEffect(() => {
    return () => {
      if (backgroundPollingRef.current) {
        clearInterval(backgroundPollingRef.current);
        backgroundPollingRef.current = null;
      }
      setIsBackgroundProcessing(false);
    };
  }, [activeSessionId]);

  // ── Mobile background/foreground recovery ─────────────────────────────────
  // When the user leaves the app (e.g. switches to another app on mobile) and
  // comes back, the SSE fetch stream is silently killed by the browser.  We
  // listen for the page becoming visible again and:
  //   1. If a stream was in progress → abort the dead stream so the UI is not
  //      stuck in a loading state, then check whether the server is still
  //      processing (it keeps running even after the client disconnects).
  //   2. Always trigger a history re-fetch so any messages that arrived while
  //      the tab was in the background are shown immediately.
  // useEffect(() => {
  //   const handleVisibilityChange = () => {
  //     if (document.visibilityState !== 'visible') return;
  //     if (!activeSessionRef.current) return;

  //     // If there was an active stream when we left, it is now dead.
  //     // Abort the controller so the UI exits the loading/streaming state cleanly.
  //     if (isStreamingRef.current && activeAbortControllerRef.current) {
  //       console.log('[visibility] Tab resumed — aborting stalled stream');
  //       isStreamingRef.current = false;
  //       activeAbortControllerRef.current.abort();
  //       activeAbortControllerRef.current = null;
  //       setAbortController(null);
  //       setIsLoading(false);

  //       // Clear any error state that the stall timer may have set while the app was
  //       // in the background — the user will see fresh history once the re-fetch below
  //       // completes, so showing an error banner here would be confusing / incorrect.
  //       hasActiveErrorRef.current = false;
  //       setMessages(prev =>
  //         prev.map(msg => msg.hasError ? { ...msg, hasError: false, errorDetails: undefined } : msg)
  //       );
  //     }

  //     // Trigger a session history reload so new messages appear immediately.
  //     // Also kicks off the background-processing check inside fetchSessionDetails,
  //     // which will re-enable the loading indicator + poll if the server is still running.
  //     console.log('[visibility] Tab resumed — refreshing session history');
  //     setRefetchTrigger(prev => prev + 1);
  //   };

  //   // ── Window blur/focus (desktop tab switching) ──────────────────────────
  //   // On desktop, switching to another window does NOT change visibilityState —
  //   // it stays 'visible' — so visibilitychange never fires.  The SSE stream can
  //   // still stall while the OS throttles the background tab.  When focus comes
  //   // back, check if an error appeared and trigger a clean recovery.
  //   // const handleWindowFocus = () => {
  //   //   if (!activeSessionRef.current) return;

  //   //   // If the stream died while focus was away and the stall timer fired the error,
  //   //   // clear the error and reload history — same recovery as visibilitychange.
  //   //   if (!isStreamingRef.current && hasActiveErrorRef.current) {
  //   //     console.log('[focus] Window regained focus — clearing stale error and reloading history');
  //   //     hasActiveErrorRef.current = false;
  //   //     setMessages(prev =>
  //   //       prev.map(msg => msg.hasError ? { ...msg, hasError: false, errorDetails: undefined } : msg)
  //   //     );
  //   //     setRefetchTrigger(prev => prev + 1);
  //   //   }
  //   // };

  //   // document.addEventListener('visibilitychange', handleVisibilityChange);
  //   // window.addEventListener('focus', handleWindowFocus);
  //   // return () => {
  //   //   document.removeEventListener('visibilitychange', handleVisibilityChange);
  //   //   window.removeEventListener('focus', handleWindowFocus);
  //   // };
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []); // Intentionally empty — uses refs so no stale-closure risk

  
  const [isInitializing, setIsInitializing] = useState(false); // Track initial page load
  const [isWaitingForSession, setIsWaitingForSession] = useState(false); // Track if we're waiting for session from URL

  // Retry state for showing retry UI
  const [retryState, setRetryState] = useState<{
    isRetrying: boolean;
    attempt: number;
    maxAttempts: number;
    reason: string;
  } | null>(null);

  // Reset chat confirmation modal state
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showResetSuccess, setShowResetSuccess] = useState(false);
  const [showResetError, setShowResetError] = useState(false);

  // External enrichment feature flag — GLOBAL by product decision (no org
  // override). Re-fetched whenever the active order changes so a flag flip
  // takes effect without a full page reload.
  const [externalEnrichment, setExternalEnrichment] = useState(false);
  useEffect(() => {
    let cancelled = false;
    import('../services/AdminService').then(({ adminService }) => {
      adminService.getGlobalFeatureFlagPublic('external_enrichment')
        .then(res => { if (!cancelled) setExternalEnrichment(res.data?.is_enabled ?? false); })
        .catch(() => { if (!cancelled) setExternalEnrichment(false); });
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getOrderObject()?.id]);

  // Read-only mode: true when viewing an order shared with the current user
  const isSharedView = searchParams.get('shared') === 'true';

  // Org-member view: the order belongs to a different user in the same org.
  // In this case the user can read the full session but cannot send messages or edit order details.
  const activeLifecycleOrder = getOrderObject();
  const hasLifecycleAssignee = activeLifecycleOrder?.owner_type === 'person';
  // Ownership model (lifecycle spec §3): a named person owns it, or nobody does
  // (agent-driven / needs an owner). Only an order owned by SOMEONE ELSE is
  // read-only; unowned orders are workable and get claimed on first reply.
  const orderOwnedByMe = !!activeLifecycleOrder && !!currentUser && (
    hasLifecycleAssignee
      ? activeLifecycleOrder.owner_uid === currentUser.uid
      : !activeLifecycleOrder.owner_type && activeLifecycleOrder.uid === currentUser.uid
  );
  const isOrgMemberView = !isSharedView && !!activeLifecycleOrder && !!currentUser && (
    hasLifecycleAssignee
      ? activeLifecycleOrder.owner_uid !== currentUser.uid
      : (!!activeLifecycleOrder.owner_type ? false : activeLifecycleOrder.uid !== currentUser.uid)
  );

  // Combined read-only flag used by ChatHeader and the input area
  const activeStage = activeLifecycleOrder ? stageOf(activeLifecycleOrder) : null;
  const isOrderCompleted = activeStage === 'completed';
  const isOrderCancelled = activeStage === 'cancelled';
  const isReadOnly = isSharedView || isOrgMemberView || isOrderCompleted || isOrderCancelled;

  // Display name for the order owner — always shown in the header.
  const orderOwnerName: string | null =
    activeLifecycleOrder?.owner_name ??
    (hasLifecycleAssignee ? null : activeLifecycleOrder?.created_by_name) ??
    (orderOwnedByMe ? (currentUser?.name ?? currentUser?.displayName ?? null) : null);
  const canTakeOrder = !isSharedView && !!activeLifecycleOrder && !!currentUser && !orderOwnedByMe && !isOrderCompleted && !isOrderCancelled;
  // Offered when a non-owner clicks something the read-only order swallows.
  const [takePromptOpen, setTakePromptOpen] = useState(false);
  const lockedOrderPrompt = useMemo(
    () => (isReadOnly && canTakeOrder ? () => setTakePromptOpen(true) : null),
    [isReadOnly, canTakeOrder],
  );

  // Multiple files upload state
  const [selectedFiles, setSelectedFiles] = useState<Array<{
    data: string;
    mimeType: string;
    preview: string;
    name: string;
    gcsUri?: string;       // set once the file is pre-uploaded to GCS
    isUploading?: boolean; // true while the upload is in flight
    uploadId?: string;     // matches a pre-upload result back to its file
  }>>([]);


  // Pre-upload files to GCS in the background. Results are matched by uploadId
  // (not array index) so removing a file mid-upload can't mislabel another one.
  const startPreUploads = useCallback((
    batch: Array<{ data: string; mimeType: string; name: string; uploadId?: string }>,
    sessionId: string | null | undefined,
  ) => {
    const patch = (uploadId: string | undefined, fields: { gcsUri?: string; isUploading: boolean }) =>
      setSelectedFiles(curr => curr.map(item => (item.uploadId === uploadId ? { ...item, ...fields } : item)));
    batch.forEach((f) => {
      if (!sessionId) {
        // No session yet — nothing to upload against; fall back to base64 on send.
        patch(f.uploadId, { isUploading: false });
        return;
      }
      ChatService.uploadChatFile(f.data, f.mimeType, f.name, sessionId)
        .then(({ gcsUri }) => patch(f.uploadId, { gcsUri, isUploading: false }))
        .catch(err => {
          console.warn('Pre-upload failed, will fall back to base64 on send:', err);
          patch(f.uploadId, { isUploading: false });
        });
    });
  }, []);

  // Legacy single image support (for backward compatibility)
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageMimeType, setSelectedImageMimeType] = useState<string | null>(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState<string | null>(null);

  // State for tracking items being added to cart from vision analysis
  const [addingToCartItems, setAddingToCartItems] = useState<Set<string>>(new Set());

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const vinProcessedRef = useRef<boolean>(false);
  const errorTooltipRef = useRef<HTMLDivElement>(null);
  const pendingVinRef = useRef<{ vinNumber: string; query: string; autoSendVin: boolean } | null>(null);
  const processedUrlSessionRef = useRef<string | null>(null); // Track processed URL session to prevent loops
  const hasProcessedInitialMessage = useRef<boolean>(false); // Track if we've already processed the initial message from HomePage
  const isSendingInitialMessage = useRef<boolean>(false); // Track if we're currently sending the initial message from HomePage
  const userHasScrolledUp = useRef<boolean>(false); // Track if user has manually scrolled up
  // Track the session ID that should receive initial data
  const initialSessionIdRef = useRef<string | null>(null);

  // Track if we are currently fetching a session to prevent duplicates
  const isFetchingSessionRef = useRef<boolean>(false);
  const fetchedSessionIdRef = useRef<string | null>(null);

  // Sync ref for active session ID to use in stable callbacks
  const activeSessionRef = useRef<string>('');
  useEffect(() => {
    activeSessionRef.current = activeSessionId;
  }, [activeSessionId]);

  // isStreamingRef will be provided by useStreamSend (instantiated after handleRetry).
  // Declare a placeholder here so all code below compiles — it will be reassigned by the hook.
  // eslint-disable-next-line prefer-const
  let isStreamingRef = useRef<boolean>(false);

  // True while we ourselves just appended `?order_id=...` to the URL via
  // `onOrderCreated` (typically right after a terminal final but before the
  // companion HITL/greeting final has been rendered). The URL effect would
  // otherwise treat this as an external navigation, bump `refetchTrigger`, and
  // `setMessages(loadedMessages)` would wipe the just-streamed bubble (and the
  // freshly attached parts_result) because the backend hasn't persisted the
  // final agent response to `conversation_history` yet — and HITL prompts are
  // never persisted at all.
  const suppressNextUrlRefetchRef = useRef<boolean>(false);

  // Tracks whether there is an active unresolved error on the last user message.
  // Used to prevent fetchSessionDetails from overwriting the error state (and hiding
  // the retry banner) when a searchParams change triggers a re-fetch right after an error.
  const hasActiveErrorRef = useRef<boolean>(false);
  // Stores the refetchTrigger value at the time the error was recorded.
  // If a new fetch arrives with a higher refetchTrigger the error guard is bypassed,
  // so an explicit Retry / visibility-resume always loads fresh history.
  const errorRefetchTriggerRef = useRef<number>(-1);

  // ── Parts-needed recovery ─────────────────────────────────────────────────
  // Some turns (e.g. when an order already exists and parts are loaded during
  // the same stream) finish with `session_part_uuids` in the structured_data
  // but `parts_result: []` in the top-level SSE event because the backend
  // populates parts into the session state AFTER emitting the final event.
  // When this is detected for a non-streaming finalized bot message that has
  // no parts_results, schedule a history reload so the fully-hydrated parts
  // are fetched from `conversation_history` via `handle_parts_expert_response_history`.
  const partsNeedRefetchRef = useRef<boolean>(false);
  useEffect(() => {
    if (isStreamingRef.current) return; // still streaming — wait
    const needsRefetch = messages.some(
      msg =>
        msg.sender === 'bot' &&
        !msg.isStreaming &&
        !msg.parts_results?.length &&
        msg.structured_data?.session_part_uuids?.length > 0,
    );
    if (needsRefetch && !partsNeedRefetchRef.current) {
      partsNeedRefetchRef.current = true;
      // Give the server a moment to persist the parts into conversation_history.
      setTimeout(() => {
        if (activeSessionRef.current) {
          console.log('[partsNeeded] Triggering history reload to hydrate parts_results');
          setRefetchTrigger(prev => prev + 1);
        }
        partsNeedRefetchRef.current = false;
      }, 2000);
    }
    if (!needsRefetch) {
      partsNeedRefetchRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // ── Complementary-parts hydration ─────────────────────────────────────────
  // Complementary suggestions are generated AFTER the search results ship
  // (deferred post-search node on the backend), so live-streamed parts cards
  // arrive with `complementary_parts` empty. One history reload after the
  // stream closes hydrates them from `conversation_history`. One-shot per
  // message id (a search may legitimately yield zero suggestions), and only
  // for streams seen in this page lifetime — never for plain history loads.
  // Attempts are counted per message id and a message only stops being a
  // candidate once a reload actually delivered its complementary parts (the
  // filter below) or the attempt budget is spent — the previous one-shot
  // marked messages "done" even when the reload was skipped (active error,
  // new stream racing in), leaving the suggestions permanently missing.
  const complementaryAttemptsRef = useRef<Map<string, number>>(new Map());
  // Message ids still awaiting their deferred suggestions — drives the reserved
  // skeleton slot in the results view (see MultiPartResultView).
  const [complementaryPending, setComplementaryPending] = useState<Set<string>>(new Set());
  const complementaryTimerPendingRef = useRef<boolean>(false);
  const hadStreamThisPageRef = useRef<boolean>(false);
  const COMPLEMENTARY_MAX_ATTEMPTS = 3;
  const COMPLEMENTARY_RESERVE_ATTEMPTS = 2;
  useEffect(() => {
    if (isStreamingRef.current) {
      hadStreamThisPageRef.current = true;
      return; // still streaming — wait
    }
    if (!hadStreamThisPageRef.current) return;
    const candidates = messages.filter(
      msg =>
        msg.sender === 'bot' &&
        !msg.isStreaming &&
        (msg.parts_results?.length ?? 0) > 0 &&
        !msg.parts_results?.some(pr => pr.complementary_parts?.length) &&
        msg.id &&
        (complementaryAttemptsRef.current.get(String(msg.id)) ?? 0) < COMPLEMENTARY_MAX_ATTEMPTS,
    );
    // Publish the candidate ids so their results view can hold the "You might
    // also need" slot open with a skeleton while we wait. Identity-stable when
    // the set is unchanged, so this never loops the render.
    // Reserve the slot only through the FIRST couple of hydration attempts. The
    // reload budget runs to 3 (2.5s + 5s + 10s backoff) because a late write is
    // still worth collecting, but a search that legitimately yields zero
    // suggestions must not shimmer at the user for ~17s — after the second
    // attempt the slot collapses and we stop promising anything.
    const pendingIds = candidates
      .filter(msg => (complementaryAttemptsRef.current.get(String(msg.id)) ?? 0) < COMPLEMENTARY_RESERVE_ATTEMPTS)
      .map(msg => String(msg.id));
    setComplementaryPending(prev => {
      // Compare against the plain array — spreading a Set needs downlevelIteration
      // under the build's ES5 target, and returning `prev` unchanged is what keeps
      // this from re-rendering on every message update.
      if (prev.size === pendingIds.length && pendingIds.every(id => prev.has(id))) return prev;
      return new Set(pendingIds);
    });
    if (!candidates.length || complementaryTimerPendingRef.current) return;
    complementaryTimerPendingRef.current = true;
    const attempt = Math.max(
      ...candidates.map(msg => complementaryAttemptsRef.current.get(String(msg.id)) ?? 0),
    );
    // Give the backend's deferred complementary node time to persist its
    // writes; back off on retries (a search may legitimately yield zero
    // suggestions — the attempt cap keeps that from looping forever).
    setTimeout(() => {
      complementaryTimerPendingRef.current = false;
      // Re-check at fire time: a NEW stream may have started while we waited.
      // Reloading (and swapping the whole message list) under an active stream
      // corrupts the in-flight bubbles and crashes React reconciliation.
      if (isStreamingRef.current || !activeSessionRef.current) return;
      candidates.forEach(msg => {
        const key = String(msg.id);
        complementaryAttemptsRef.current.set(
          key, (complementaryAttemptsRef.current.get(key) ?? 0) + 1,
        );
      });
      console.log('[complementaryHydrate] Triggering history reload to hydrate complementary parts');
      setRefetchTrigger(prev => prev + 1);
    }, 2500 * Math.pow(2, attempt));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Debug: Track when messages state changes
  useEffect(() => {
    console.log('Messages state updated, count:', messages.length);
    // Keep hasActiveErrorRef in sync: set when the last message has an error, clear otherwise.
    const lastMsg = messages[messages.length - 1];
    const nowHasError = !!(lastMsg?.hasError);
    hasActiveErrorRef.current = nowHasError;
    // Record which refetchTrigger cycle produced this error so that a subsequent
    // explicit refetch (higher trigger value) can bypass the guard.
    if (nowHasError) {
      errorRefetchTriggerRef.current = refetchTrigger;
    }
  }, [messages, refetchTrigger]);


  // Handle session ID from URL parameter - URL is the source of truth
  useEffect(() => {
    let ignore = false;

    const processUrlParams = async () => {
      // Get session ID from URL path parameter (primary) or query param (fallback)
      const sessionIdFromUrl = urlSessionId || searchParams.get('session_id');
      const orderIdFromUrl = searchParams.get('order_id');

      const navigationState = location.state as any;
      const hasInitialData = navigationState?.prompt || navigationState?.files;

      console.log('ChatPage URL params:', {
        urlSessionId,
        sessionIdFromUrl,
        orderIdFromUrl,
        currentActiveSessionId: activeSessionId,
        currentActiveOrderId: getActiveOrderId(),
        hasInitialData,
        navigationState: navigationState ? { hasPrompt: !!navigationState.prompt, hasFiles: !!navigationState.files } : null
      });

      // Handle order from URL first
      if (orderIdFromUrl) {
        const orderId = parseInt(orderIdFromUrl, 10);
        if (!isNaN(orderId) && orderId !== getActiveOrderId()) {
          try {
            const order = await orderService.getOrderById(orderId);
            if (!ignore) {
              setActiveOrderId(order.id);
              console.log('[processUrlParams] loaded order on mount/refresh — translated_descriptions:',
                order.parts_in_order?.items?.map((i: any) => ({ key: i.id || i.part_id, translated_description: i.translated_description }))
              );
              setOrderObject(order);
            }
          } catch (error) {
            console.error('Failed to load order from URL:', error);
            if (!ignore) {
              removeActiveOrderId();
              setOrderObject(null);
            }
          }
        }
      } else if (getActiveOrderId() !== null && !ignore) {
        // Clear order if not in URL
        removeActiveOrderId();
        setOrderObject(null);
      }

      // Handle session ID - update active session if different
      if (sessionIdFromUrl && sessionIdFromUrl !== activeSessionId && !ignore) {
        setIsWaitingForSession(true);
        setActiveSessionId(sessionIdFromUrl);
      } else if (sessionIdFromUrl && sessionIdFromUrl === activeSessionId && !ignore) {
        // Same session navigated to again (e.g. coming back from guest leads page, or
        // searchParams changed mid-stream when order_id is appended to the URL by
        // fetchSessionDetails). Guard: do NOT refetch while a message is being streamed
        // because setMessages(loadedMessages) would overwrite the optimistic messages and
        // produce duplicate / missing entries.
        //
        // Also skip when we ourselves just appended `?order_id=...` via onOrderCreated.
        // The backend hasn't yet persisted the in-flight final/HITL response to
        // `conversation_history`, so a refetch here wipes the just-rendered bubble
        // (and any parts_result attached to it). The flag is one-shot.
        if (suppressNextUrlRefetchRef.current) {
          suppressNextUrlRefetchRef.current = false;
        } else if (!isStreamingRef.current) {
          setRefetchTrigger(prev => prev + 1);
        }
      } else if (!sessionIdFromUrl && activeSessionId && !ignore) {
        // No session in URL - clear active session
        setActiveSessionId('');
        setIsWaitingForSession(false);
        setIsInitializing(false);
      } else if (!sessionIdFromUrl && !ignore) {
        setIsInitializing(false);
        setIsWaitingForSession(false);
      }
    };

    processUrlParams();

    return () => {
      ignore = true;
    };
  }, [urlSessionId, searchParams]); // React to URL changes only; isStreamingRef (a ref, not state) guards against refetch during active streaming

  // Close mobile panels when a session is selected on mobile
  useEffect(() => {
    if (activeSessionId && window.innerWidth < 1024) { // lg breakpoint
      setShowMobileOrders(false);
    }
  }, [activeSessionId]);

  // Refresh trigger for refetching the associated order (keeps sidebar in sync)
  const [orderRefreshTrigger, setOrderRefreshTrigger] = useState(0);

  // Guest history: conversation from the original guest session (when order source === 'guest')
  // Populated from session state (guest_conversation_history) during fetchSessionDetails.
  const [guestHistory, setGuestHistory] = useState<ConversationMessage[]>([]);
  const [guestHistoryBuyer, setGuestHistoryBuyer] = useState<string | null>(null);

  const refreshAssociatedOrder = async () => {
    // Directly fetch and update the associated order without triggering session reload
    // IMPORTANT: Only refresh the order that's specified in the URL to prevent switching orders
    // Read the LIVE URL: `searchParams` is this render's snapshot, and right
    // after onOrderCreated navigated it still lacks order_id — the fallback
    // below then navigated a second time WITHOUT the refetch guard, the URL
    // effect reloaded history mid-turn and the just-streamed marked photo
    // vanished until a later reload brought it back.
    const urlOrderId = new URLSearchParams(window.location.search).get('order_id');

    if (urlOrderId) {
      const orderId = parseInt(urlOrderId, 10);
      if (!isNaN(orderId)) {
        try {
          const updatedOrder = await orderService.getOrderById(orderId);
          setActiveOrderId(orderId);
          setOrderObject(updatedOrder);
          setOrderRefreshTrigger((prev) => prev + 1); // Increment for sidebar updates
        } catch (error) {
          console.error('Error refreshing associated order:', error);
        }
      }
    } else if (getActiveOrderId()) {
      // Fallback: if no order in URL but we have one in context, fetch it and add to URL
      try {
        const orderId = getActiveOrderId()!;
        const updatedOrder = await orderService.getOrderById(orderId);
        setActiveOrderId(orderId);
        setOrderObject(updatedOrder);
        setOrderRefreshTrigger((prev) => prev + 1);
        // Add order_id to the URL with new pattern. Our own navigation: the
        // messages on screen are current, so the URL effect must not reload them.
        if (new URLSearchParams(window.location.search).get('order_id') !== String(orderId)) {
          const newUrl = `/chat/${activeSessionId}?order_id=${orderId}`;
          suppressNextUrlRefetchRef.current = true;
          navigate(newUrl, { replace: true, state: location.state });
        }
      } catch (error) {
        console.error('Error refreshing associated order:', error);
      }
    }
  };


  // Pre-check for initial message to set flag before session loads
  useEffect(() => {
    const navigationState = location.state as any;

    // Set the flag immediately if we have initial data, BEFORE the session loads
    if (activeSessionId && navigationState && !hasProcessedInitialMessage.current) {
      const initialPrompt = navigationState.prompt;
      const initialFiles = navigationState.files;

      if (initialPrompt || (initialFiles && initialFiles.length > 0)) {
        console.log('Detected initial message from HomePage - setting flag early');
        isSendingInitialMessage.current = true; // Set this EARLY to prevent message clearing
        initialSessionIdRef.current = activeSessionId; // Lock this session ID as the one with initial data
      }
    }
  }, [activeSessionId, location.state]);

  // Fetch session details and any associated order when activeSessionId changes
  useEffect(() => {
    let ignore = false;
    // Snapshot refetchTrigger for this closure so we can compare inside the async function.
    const currentRefetchTrigger = refetchTrigger;

    const fetchSessionDetails = async () => {
      // Don't fetch if no session ID
      if (!activeSessionId) {
        // Clear messages when there's no session
        console.log('No active session - clearing messages');
        setMessages([]);
        setContextTags([]);
        return;
      }

      console.log('🔄 Starting session fetch for:', activeSessionId);

      // Check navigation state - don't use cache if we have initial data pending
      const navigationState = location.state as any;

      try {
        isFetchingSessionRef.current = true;

        console.log('🌐 Fetching session from server:', activeSessionId);

        setIsLoadingHistory(true);

        const sessionDetails = await ChatService.getSessionDetails(activeSessionId);

        if (ignore) {
          console.warn('⏭️ Session fetch result ignored (stale):', activeSessionId, '- User navigated to different session');
          return;
        }

        console.log('✅ Session details fetched:', {
          sessionId: activeSessionId,
          status: sessionDetails.status,
          hasSession: !!sessionDetails.session,
          historyLength: sessionDetails.session?.conversation_history?.length || 0
        });

        if (sessionDetails.status === 'success' && sessionDetails.session) {
          const conversationHistory = sessionDetails.session.conversation_history || [];

          // Mark as successfully fetched
          fetchedSessionIdRef.current = activeSessionId;

          // Check navigation state for initial data from HomePage
          const navigationState = location.state as any;
          const hasInitialData = navigationState?.prompt || (navigationState?.files && navigationState.files.length > 0);

          // If the session already has conversation history but navigation state still carries
          // initial data (e.g. after a hard page refresh), clear that stale state immediately
          // and mark as processed so the initial-message effect does NOT re-send the message.
          if (conversationHistory.length > 0 && hasInitialData) {
            console.log('🔄 Session already has history with stale navigation state — clearing to prevent re-send on refresh');
            hasProcessedInitialMessage.current = true;
            isSendingInitialMessage.current = false;
            navigate(location.pathname + location.search, { replace: true, state: {} });
          }

          // If this is a NEW session with initial data (pending or already being sent),
          // SKIP loading empty history to prevent overwriting messages added by sendMessageWithInitialData.
          // This covers both cases:
          // 1. Initial message not yet processed: we'll wait for it to be sent
          // 2. Initial message already sent: the server hasn't saved it to history yet
          if (conversationHistory.length === 0 && hasInitialData) {
            console.log('🎯 Skipping empty history load - initial data present (processed:', hasProcessedInitialMessage.current, ')');
            // Don't set messages to [] - keep whatever is currently shown
            // Load context tags and other session state, but not messages
            if (sessionDetails.session.state?.context_tags) {
              setBackendTags(sessionDetails.session.state.context_tags);
            }

            // Mark that we're ready for the initial message
            setIsLoadingHistory(false);
            setIsInitializing(false);
            setIsWaitingForSession(false);
            isFetchingSessionRef.current = false;

            console.log('✨ Session ready for initial message - flags cleared');
            return; // Exit early without setting messages
          }

          // Processing messages...
          const loadedMessagesRaw: Message[] = conversationHistory.map((msg: any) => {
            let messageText = msg.text;
            if (msg.sender === 'bot' && msg.structured_data) {
              const structuredData = msg.structured_data;
              messageText = structuredData.text_message || messageText;
            }

            let structuredData = msg.structured_data || null;

            let parts_results: PartResult[] | undefined = undefined;
            if (msg.parts_result && Array.isArray(msg.parts_result) && msg.parts_result.length > 0) {
              parts_results = msg.parts_result;
            } else if (msg.image || msg.parts_list) {
              parts_results = [{
                image: msg.image,
                image_callout: msg.image_callout,
                part_callout_id: msg.part_callout_id,
                complementary_parts: msg.parts_list,
                structured_data: structuredData,
              }];
            }

            return {
              id: msg.id || newMessageId('hist'),
              text: messageText,
              parts_results: parts_results,
              context_tags: msg.context_tags || null,
              vin: msg.vin || null,
              datasetId: null,
              sender: msg.sender as 'user' | 'bot',
              timestamp: new Date(msg.timestamp * 1000),
              structured_data: structuredData,
              annotated_images: msg.annotated_images || null,
              vision_part_images: msg.vision_part_images || null,
              // Request-line ledger, replayed per message so a reloaded
              // conversation shows each table exactly as it was (ADR 0007).
              lines: msg.lines || null,
              turn_lines: msg.turn_lines || null,
              repair_instructions: msg.repair_instructions || null,
              search_summarization: msg.search_summarization || null,
              isStreaming: false,
              isError: false,
              events: undefined,
              uploadedFiles: msg.uploadedFiles || [],
              callToAction: msg?.callToAction || msg?.call_to_action || null,
              cta_actions: msg?.cta_actions || null,
              // "took N s" survives the history reloads: the backend persists the
              // turn's timing and hands it back on the turn's last bubble.
              turnTiming: msg.turn_timing
                ? { clientMs: msg.turn_timing.terminal_final_ms ?? msg.turn_timing.total_ms, server: msg.turn_timing }
                : null,
            };
          });

          // Hide duplicate user bubbles re-persisted by 429/503 retries.
          const loadedMessages: Message[] = markHiddenClarificationAnswers(
            dropRetryDuplicateUserMessages(loadedMessagesRaw),
          );

          // Logic for setting messages
          // Skip setting messages if we're actively sending the initial message from HomePage.
          // NOTE: Do NOT use `messages.length` here - it's from a stale closure and will always
          // be 0 from the initial render, causing a race condition that overwrites messages.
          if (isSendingInitialMessage.current) {
            console.log('Skipping message load - initial message is being sent');
          } else if (isStreamingRef.current) {
            // Skip overwriting messages while a stream is in progress (e.g. a refetch triggered
            // by a URL searchParams change such as order_id being appended mid-stream would
            // otherwise overwrite the optimistic messages and cause visible duplicates).
            console.log('Skipping message load - stream is in progress (isLoading=true)');
          } else if (hasActiveErrorRef.current && currentRefetchTrigger <= errorRefetchTriggerRef.current) {
            // Skip overwriting messages while there is an unresolved error AND this fetch
            // was not explicitly triggered (e.g. a searchParams change such as order_id
            // being appended to the URL fires a re-fetch that would wipe the hasError flag
            // and hide the retry banner).
            // If refetchTrigger has advanced since the error (Retry button, visibility
            // resume, etc.) we allow the load so fresh history is shown.
            console.log('Skipping message load - active error present, preserving retry state');
          } else {
            // A turn's marked photo may not be in conversation_history yet when
            // a post-turn reload lands — keep that live bubble until it is.
            setMessages(prev => keepUnsavedPhotoBubbles(prev, loadedMessages));

            // Restore context tags from the last bot message that carried them,
            // falling back to session-state tags.  This ensures the ContextTagsBar
            // always reflects the most recent parts-search result after a page reload.
            const lastTaggedMessage = [...loadedMessages]
              .reverse()
              .find(m => m.sender === 'bot' && m.context_tags && (m.context_tags as string[]).length > 0);
            const tagsToRestore =
              (lastTaggedMessage?.context_tags as string[] | null) ||
              sessionDetails.session.state?.context_tags ||
              null;
            if (tagsToRestore && tagsToRestore.length > 0) {
              setBackendTags(tagsToRestore);
            }

            // ── Background-processing detection ──────────────────────────────────
            // Check if the server is still running the action loop (client disconnected
            // mid-stream and came back).  If so, show the indicator and poll until done.
            // This only applies on mobile — on desktop the SSE stream stays alive so
            // background polling is unnecessary and would waste resources.
            const isMobileDevice = window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
            if (!ignore && isMobileDevice) {
              try {
                const streamStatus = await ChatService.getStreamingStatus(activeSessionId);
                if (streamStatus.is_streaming && !isStreamingRef.current) {
                  console.log('🔄 Server is still processing in background — starting poll');
                  setIsBackgroundProcessing(true);
                  setIsLoading(true);

                  // Clear any previous polling interval
                  if (backgroundPollingRef.current) {
                    clearInterval(backgroundPollingRef.current);
                  }

                  const sessionIdSnapshot = activeSessionId;
                  // Safety cap: stop polling after 10 minutes regardless — this covers
                  // the case where the backend is stuck and its own timeout hasn't fired
                  // yet (e.g. the backend process itself was restarted in the middle of
                  // a stream and the new instance never gets the is_streaming=True flag).
                  const POLL_TIMEOUT_MS = 10 * 60 * 1000;
                  const pollStartedAt = Date.now();

                  backgroundPollingRef.current = setInterval(async () => {
                    // Stop if the user navigated away or started a new stream
                    if (ignore || isStreamingRef.current) {
                      clearInterval(backgroundPollingRef.current!);
                      backgroundPollingRef.current = null;
                      return;
                    }
                    // Hard timeout — backend crashed and never cleared the flag
                    if (Date.now() - pollStartedAt > POLL_TIMEOUT_MS) {
                      console.warn('⚠️ Background processing poll timed out — stopping indicator');
                      clearInterval(backgroundPollingRef.current!);
                      backgroundPollingRef.current = null;
                      setIsBackgroundProcessing(false);
                      setIsLoading(false);
                      // Reload history — any completed iterations will be visible
                      setRefetchTrigger(prev => prev + 1);
                      return;
                    }
                    try {
                      const status = await ChatService.getStreamingStatus(sessionIdSnapshot);
                      if (!status.is_streaming) {
                        console.log('✅ Background processing done — reloading history');
                        clearInterval(backgroundPollingRef.current!);
                        backgroundPollingRef.current = null;
                        setIsBackgroundProcessing(false);
                        setIsLoading(false);
                        // Trigger a full history reload to display the completed messages
                        setRefetchTrigger(prev => prev + 1);
                      }
                    } catch (pollErr) {
                      console.warn('Streaming status poll error:', pollErr);
                    }
                  }, 3000);
                }
              } catch (statusErr) {
                // Non-critical — just log and continue without polling
                console.warn('Could not check streaming status:', statusErr);
              }
            }
          }

          // Load guest conversation history.
          // Case 1: session was created via convert_lead_to_order — history is embedded
          //         in the session state under guest_conversation_history.
          // Case 2: pure guest session (convert_to_order path, APP_NAME_GUEST) — the
          //         session has no guest_conversation_history in state, but the order
          //         source is 'guest'.  In that case treat the whole conversation_history
          //         as the guest history so the org-member sees it as a read-only
          //         "Client History" section and can continue the chat with a blank input.
          const rawGuestHistory = (sessionDetails.session as any).guest_conversation_history;
          const orderObject = getOrderObject();
          // isPureGuestSession: only treat as read-only guest view when the order source
          // is 'guest' AND the current user is NOT the owner (org-member viewing someone
          // else's order).  After "Take Ownership" the uid matches and we must show the
          // full conversation history to the new owner instead of an empty chat.
          const isCurrentUserOwner = !orderObject || !currentUser || orderObject.uid === currentUser.uid;
          const isPureGuestSession =
            !rawGuestHistory?.length &&
            orderObject?.source === 'guest' &&
            conversationHistory.length > 0 &&
            !isCurrentUserOwner;

          if (rawGuestHistory && rawGuestHistory.length > 0) {
            // Case 1: converted lead — guest history is embedded in session state
            setGuestHistory(rawGuestHistory);
            setGuestHistoryBuyer(
              sessionDetails.session.state?.guest_visitor_name ||
              sessionDetails.session.state?.buyer ||
              null
            );
          } else if (isPureGuestSession) {
            // Case 2: pure guest session opened by an org member — show conversation
            //         as read-only client history, keep main messages area empty
            setGuestHistory(conversationHistory as any);
            setGuestHistoryBuyer(
              sessionDetails.session.state?.buyer ||
              orderObject?.buyer ||
              null
            );
            // Override the messages set above — org member should see an empty chat
            // so they can continue the conversation, not replay the guest messages.
            if (!isSendingInitialMessage.current && !isStreamingRef.current) {
              setMessages([]);
            }
          } else {
            setGuestHistory([]);
            setGuestHistoryBuyer(null);
          }

          // Check if session has an associated order and load it
          // IMPORTANT: Read order_id directly from the live URL (not the stale searchParams
          // closure) because searchParams is captured at effect-registration time and will be
          // stale by the time this async code runs after a navigation.
          const liveSearchParams = new URLSearchParams(window.location.search);
          const orderIdFromUrl = liveSearchParams.get('order_id');
          const sessionOrderId = sessionDetails.session.order_id;

          if (sessionOrderId && !orderIdFromUrl) {
            // Session has an order but it's not in the URL - redirect immediately with order_id
            console.log('Order found in session, redirecting with order_id:', sessionOrderId);
            const newUrl = `/chat/${activeSessionId}?order_id=${sessionOrderId}`;
            // History was just loaded by this very fetch — adding order_id to
            // the URL must not trigger a second load (it re-signs every image
            // URL, so the marked photo blinks out and back).
            suppressNextUrlRefetchRef.current = true;
            navigate(newUrl, { replace: true, state: location.state });
            // Also eagerly load the order into context so the sidebar is populated
            try {
              const order = await orderService.getOrderById(sessionOrderId);
              if (!ignore) {
                setActiveOrderId(order.id);
                setOrderObject(order);
              }
            } catch (error) {
              console.error('Failed to load order from session:', error);
            }
          } else if (getActiveOrderId() && !orderIdFromUrl) {
            // We have an order in context but not in URL - fetch it
            try {
              const order = await orderService.getOrderById(getActiveOrderId()!);
              if (!ignore) {
                setActiveOrderId(order.id);
                setOrderObject(order);
              }
            } catch (error) {
              console.error('Failed to refresh order:', error);
            }
          } else if (!sessionOrderId && !orderIdFromUrl && getActiveOrderId()) {
            // Session has no order and URL has no order, but context has one - clear it
            console.log('Clearing order - session has no associated order');
            removeActiveOrderId();
            setOrderObject(null);
          }

          // Reset VIN processing flags for new sessions
          if (!pendingVinRef.current) {
            vinProcessedRef.current = false;
            setHasProcessedVin(false);
            setIsProcessingVin(false);
          } else {
            // Handle pending VIN
            const checkAndTrigger = () => {
              if (isLoadingHistory || isLoading) {
                setTimeout(checkAndTrigger, 200);
              } else {
                setVinAutoSendTrigger(prev => prev + 1);
              }
            };
            setTimeout(checkAndTrigger, 100);
          }

          setTimeout(() => scrollToBottom(true), 1000);
        } else {
          console.warn('Session details fetch failed:', sessionDetails);
        }

      } catch (err) {
        console.error('Error fetching session details:', err);
      } finally {
        isFetchingSessionRef.current = false;

        if (!ignore) {
          setIsLoadingHistory(false);
          setIsInitializing(false);
          setIsWaitingForSession(false);
        }
      }
    };

    fetchSessionDetails();

    return () => {
      ignore = true;
    };
  }, [activeSessionId, refetchTrigger]);



  // Handle initial message and files from HomePage navigation
  useEffect(() => {
    const navigationState = location.state as any;

    console.log('Initial message effect:', {
      activeSessionId,
      hasNavigationState: !!navigationState,
      hasProcessed: hasProcessedInitialMessage.current,
      isLoading,
      isLoadingHistory,
      isWaitingForSession
    });

    // Check if we have a prompt or files from the HomePage and haven't processed them yet
    // Wait until session is loaded (not loading history AND not waiting for session) before auto-sending
    if (activeSessionId && navigationState && !hasProcessedInitialMessage.current && !isLoading && !isLoadingHistory && !isWaitingForSession) {
      const initialPrompt = navigationState.prompt;
      const initialFiles = navigationState.files;

      if (initialPrompt || (initialFiles && initialFiles.length > 0)) {
        console.log('Processing initial message from HomePage:', { initialPrompt, filesCount: initialFiles?.length });

        // Mark as processed to prevent duplicate sends
        hasProcessedInitialMessage.current = true;
        // Clear navigation state to prevent re-send on hard refresh
        navigate(location.pathname + location.search, { replace: true, state: {} });
        // Flag already set in the pre-check effect above

        // Auto-send the message immediately since session is ready
        console.log('Session ready, sending initial message from HomePage');
        if (initialPrompt || (initialFiles && initialFiles.length > 0)) {
          sendMessageWithInitialData(initialPrompt || '', initialFiles || []);
        }
      }
    }
  }, [activeSessionId, location.state, isLoading, isLoadingHistory, isWaitingForSession]);

  // Reset the initial message flag when navigating away from a session
  useEffect(() => {
    // Only reset the flag when there's no active session
    // This allows re-processing if the user creates a new session
    if (!activeSessionId) {
      hasProcessedInitialMessage.current = false;
      isSendingInitialMessage.current = false;
    }

    // Also reset the flag if there's no navigation state with initial data
    const navigationState = location.state as any;
    if (activeSessionId && (!navigationState || (!navigationState.prompt && !navigationState.files?.length))) {
      isSendingInitialMessage.current = false;
    }
  }, [activeSessionId, location.state]);

  // Close error tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (errorTooltipRef.current && !errorTooltipRef.current.contains(event.target as Node)) {
        setExpandedErrors(new Set());
      }
    };

    if (expandedErrors.size > 0) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expandedErrors]);


  /** The scrolling viewport — the wrapper tagged data-chat-scroll below.
   *  Two ancestors carry overflow-y-auto (that wrapper and MessageList's own
   *  root); only the wrapper has a constrained height, so closest('.overflow-y-auto')
   *  picked the inner one, which never overflows, and the scroll went nowhere.
   *  The older "walk up until an ancestor overflows" found the wrapper on tall
   *  threads but returned null on short ones. */
  const getScrollContainer = (): HTMLElement | null =>
    (messagesEndRef.current?.closest('[data-chat-scroll]') as HTMLElement | null)
    ?? (messagesEndRef.current?.closest('.overflow-y-auto') as HTMLElement | null)
    ?? null;

  const scrollToBottom = (force: boolean = false) => {
    const container = getScrollContainer();
    if (!container) {
      messagesEndRef.current?.scrollIntoView({ behavior: force ? 'auto' : 'smooth', block: 'end' });
      return;
    }
    // Double rAF: let React commit and the browser lay out before measuring.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (force) container.scrollTop = container.scrollHeight;
      else container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }));
  };

  // Stick to the bottom unless the reader has scrolled away from it.
  //
  // The observer watches the CONTENT BOX, not the `messages` array: most of the
  // growth lands after React has committed — streaming steps, a marked photo
  // finishing its load, the deferred complementary round — so an effect keyed on
  // `messages` measured a height that did not exist yet and stopped short.
  // A reader who scrolled up keeps their place (the 150px flag below), which is
  // what stops the old "screen jump" while they read an earlier result.
  useEffect(() => {
    const container = getScrollContainer();
    const content = messagesEndRef.current?.parentElement;
    if (!container || !content) return;

    const handleScroll = () => {
      userHasScrolledUp.current =
        container.scrollHeight - container.scrollTop - container.clientHeight > 150;
    };
    container.addEventListener('scroll', handleScroll, { passive: true });

    const observer = new ResizeObserver(() => {
      if (!userHasScrolledUp.current) scrollToBottom(true);
    });
    observer.observe(content);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [activeSessionId, isLoadingHistory, messages.length > 0]);

  // Sending pulls the reader back to the live end of the thread.
  useEffect(() => {
    if (isLoading) userHasScrolledUp.current = false;
  }, [isLoading]);

  // Focus the chat input once the session/history has finished loading
  useEffect(() => {
    if (!isLoadingHistory && !isWaitingForSession && activeSessionId && !isReadOnly) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isLoadingHistory, isWaitingForSession, activeSessionId, isReadOnly]);

  // Helper to check if a session is still active
  const isSessionActive = (targetSessionId: string): boolean => {
    return targetSessionId === activeSessionRef.current;
  };

  // Warn user before leaving page while processing (external navigation like closing tab/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isLoading) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // Some browsers display this message
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isLoading]);

  // Block internal navigation when processing (browser back/forward buttons)
  useEffect(() => {
    if (!isLoading) return;

    const handlePopState = (e: PopStateEvent) => {
      if (isLoading) {
        const shouldLeave = window.confirm(
          'The AI is currently processing your request. Leaving this page will stop the processing. Are you sure you want to leave?'
        );

        if (!shouldLeave) {
          // Push the current state back to prevent navigation
          window.history.pushState(null, '', window.location.href);
        } else {
          // User confirmed, abort the stream
          if (abortController) {
            abortController.abort();
          }
        }
      }
    };

    // Push a dummy state to enable popstate detection
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isLoading, abortController]);

  // Block React Router navigation when processing (intercept navigate calls)
  useEffect(() => {
    if (!isLoading) return;

    // Store the original navigate function
    const originalNavigate = navigate;

    // Note: We can't actually override the navigate function that's already been captured
    // by React Router links. Instead, we'll intercept link clicks.
    const handleLinkClick = (e: MouseEvent) => {
      if (!isLoading) return;

      // Check if the click target or any parent is a link
      let target = e.target as HTMLElement;
      while (target && target !== document.body) {
        if (target.tagName === 'A' || target.closest('a')) {
          const link = target.tagName === 'A' ? target : target.closest('a');
          const href = link?.getAttribute('href');

          // Only intercept internal links (not external or anchor links)
          if (href && href.startsWith('/') && !href.startsWith('/#')) {
            e.preventDefault();
            e.stopPropagation();

            const shouldLeave = window.confirm(
              'The AI is currently processing your request. Leaving this page will stop the processing. Are you sure you want to leave?'
            );

            if (shouldLeave) {
              // User confirmed, abort the stream and navigate
              if (abortController) {
                abortController.abort();
              }
              navigate(href);
            }
            return;
          }
        }
        target = target.parentElement as HTMLElement;
      }
    };

    // Capture link clicks during the capture phase
    document.addEventListener('click', handleLinkClick, true);

    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [isLoading, abortController, navigate]);

  // Function to stop streaming
  const stopStreaming = useCallback(() => {
    if (abortController) {
      // Clear the streaming flag immediately so fetchSessionDetails can reload history
      isStreamingRef.current = false;
      abortController.abort();
      setAbortController(null);
      activeAbortControllerRef.current = null;
      setIsLoading(false);

      // Find the last user message to restore it to input
      setMessages(prev => {
        // Find the last user message
        const lastUserMessage = [...prev].reverse().find(msg => msg.sender === 'user');

        // Restore the last user message to the input field
        if (lastUserMessage) {
          setInputMessage(lastUserMessage.text);
        }

        // Remove the last user message and any streaming bot messages
        return prev
          .filter(msg => !(msg.isStreaming || (lastUserMessage && msg.id === lastUserMessage.id)))
          .map(msg => ({
            ...msg,
            isStreaming: false,
            currentStep: undefined
          }));
      });

      // setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [abortController, isStreamingRef]);

  // Memoize helper function to validate image data
  const isValidImageData = React.useCallback((imageData: string | null): boolean => {
    if (!imageData) return false;

    // Check if it's a base64 data URL
    if (imageData.startsWith('data:image/')) {
      // Extract the base64 part after the comma
      const base64Data = imageData.split(',')[1];
      if (!base64Data) return false;

      // Calculate approximate size in bytes (base64 is ~33% larger than original)
      const sizeInBytes = (base64Data.length * 3) / 4;
      const sizeInKB = sizeInBytes / 1024;

      // Check if size is more than 1KB (very small images are likely incomplete)
      return sizeInKB > 1;
    }

    // For regular URLs, assume they're valid
    return imageData.length > 50;
  }, []);

  // Attach a file that already lives in GCS (e.g. an insurance case file) to
  // the composer by its gs:// URI — the send path reads gcsUri directly, so no
  // browser fetch of the signed URL (GCS blocks that via CORS) and no re-upload.
  // The signed URL is only used for the composer thumbnail preview.
  const attachRemoteFile = useCallback((gcsUri: string, previewUrl: string, name: string, mime: string) => {
    if (selectedFiles.length >= 10) {
      alert(t('chat.errors.tooManyFiles', { defaultValue: 'You can upload a maximum of 10 files at once.', count: 10 }));
      return;
    }
    setSelectedFiles(prev => [...prev, {
      data: '',
      mimeType: mime,
      preview: previewUrl,
      name,
      gcsUri,
      isUploading: false,
      uploadId: newUploadId(),
    }]);
  }, [selectedFiles.length, t]);

  // Multiple files upload handlers
  const handleImageSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB per file
    const maxFiles = 10; // Maximum number of files

    // Check if adding these files would exceed the limit
    if (selectedFiles.length + files.length > maxFiles) {
      alert(t('chat.errors.tooManyFiles', {
        defaultValue: `You can upload a maximum of ${maxFiles} files at once.`,
        count: maxFiles
      }));
      return;
    }

    const newFiles: Array<{ data: string; mimeType: string; preview: string; name: string; gcsUri?: string; isUploading?: boolean }> = [];

    // Filter valid files first
    const validFiles = Array.from(files).filter((file) => {
      // Validate file type
      if (!validTypes.includes(file.type)) {
        alert(t('chat.errors.invalidImageType', {
          defaultValue: 'Please select valid image files (JPEG, PNG, GIF, or WebP)',
          fileName: file.name
        }));
        return false;
      }

      // Validate file size
      if (file.size > maxSize) {
        alert(t('chat.errors.imageTooLarge', {
          defaultValue: `Image "${file.name}" is too large. Maximum size is 10MB.`,
          fileName: file.name
        }));
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    let filesProcessed = 0;

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        const base64Data = result.split(',')[1];

        newFiles.push({
          data: base64Data,
          mimeType: file.type,
          preview: result,
          name: file.name,
          isUploading: true,
        });

        filesProcessed++;

        // Once all valid files are processed, update state then kick off GCS pre-uploads
        if (filesProcessed === validFiles.length) {
          // Side effects must stay OUT of the state updater: React StrictMode
          // runs updaters twice in dev, which uploaded every file twice.
          const batch = newFiles.map((f) => ({ ...f, uploadId: newUploadId() }));
          setSelectedFiles(prev => [...prev, ...batch]);
          startPreUploads(batch, activeSessionId);
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset the input so the same files can be selected again
    event.target.value = '';
  }, [selectedFiles.length, t, activeSessionId, startPreUploads]);

  // Unified file upload handler for images, PDF and TXT files
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const documentTypes = ['application/pdf', 'text/plain'];
    const validTypes = [...imageTypes, ...documentTypes];
    const maxSize = 10 * 1024 * 1024; // 10MB per file
    const maxFiles = 10; // Maximum number of files

    // Check if adding these files would exceed the limit
    if (selectedFiles.length + files.length > maxFiles) {
      alert(t('chat.errors.tooManyFiles', {
        defaultValue: `You can upload a maximum of ${maxFiles} files at once.`,
        count: maxFiles
      }));
      return;
    }

    const newFiles: Array<{ data: string; mimeType: string; preview: string; name: string; gcsUri?: string; isUploading?: boolean }> = [];

    // Filter valid files first
    const validFiles = Array.from(files).filter((file) => {
      // Validate file type
      if (!validTypes.includes(file.type)) {
        alert(t('chat.errors.invalidFileType', {
          defaultValue: `Please select valid files (Images: JPEG, PNG, GIF, WebP or Documents: PDF, TXT). "${file.name}" is not supported.`,
          fileName: file.name
        }));
        return false;
      }

      // Validate file size
      if (file.size > maxSize) {
        alert(t('chat.errors.fileTooLarge', {
          defaultValue: `File "${file.name}" is too large. Maximum size is 10MB.`,
          fileName: file.name
        }));
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    let filesProcessed = 0;

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;

        // Determine preview based on file type
        let preview: string;
        if (imageTypes.includes(file.type)) {
          // For images, use the actual image as preview
          preview = result;
        } else if (file.type === 'application/pdf') {
          // PDF placeholder
          preview = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VmNDQ0NCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjI0IiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRzZSI+UERFPC90ZXh0Pjwvc3ZnPg==';
        } else {
          // TXT placeholder
          preview = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iIzM0OThkYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjI0IiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+VFhUPC90ZXh0Pjwvc3ZnPg==';
        }

        newFiles.push({
          data: result.split(',')[1] || '', // Extract base64 data without the data URL prefix
          mimeType: file.type,
          preview: preview,
          name: file.name,
          isUploading: true,
        });

        filesProcessed++;

        // Once all valid files are processed, update state then kick off GCS pre-uploads
        if (filesProcessed === validFiles.length) {
          // Side effects must stay OUT of the state updater: React StrictMode
          // runs updaters twice in dev, which uploaded every file twice.
          const batch = newFiles.map((f) => ({ ...f, uploadId: newUploadId() }));
          setSelectedFiles(prev => [...prev, ...batch]);
          startPreUploads(batch, activeSessionId);
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset the input so the same files can be selected again
    event.target.value = '';
  }, [selectedFiles.length, t, activeSessionId, startPreUploads]);

  const clearSelectedFile = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearAllSelectedFiles = () => {
    setSelectedFiles([]);
  };

  // Legacy single image clear (for backward compatibility)
  const clearSelectedImage = useCallback(() => {
    setSelectedImage(null);
    setSelectedImageMimeType(null);
    setSelectedImagePreview(null);
  }, []);

  // Callback for retry notifications
  const handleRetry = useCallback((attempt: number, maxAttempts: number, reason: string) => {
    console.log(`Retry attempt ${attempt}/${maxAttempts}: ${reason}`);
    setRetryState({
      isRetrying: true,
      attempt,
      maxAttempts,
      reason,
    });

    // Auto-clear retry state after 5 seconds
    setTimeout(() => {
      setRetryState(null);
    }, 5000);
  }, []);

  // ── Streaming lifecycle hook ──────────────────────────────────────────────
  // Centralises isStreamingRef management, AbortController creation, and all
  // onComplete/onError/catch paths so no send function can forget to reset the flag.
  const { send: streamSend, abort: streamAbort, isStreamingRef: streamingRef } = useStreamSend({
    setMessages,
    setIsLoading,
    setRetryState,
    activeAbortControllerRef,
    setAbortController,
    isSessionActive,
    onRetry: handleRetry,
    // When the stream errors out, wait a few seconds then reload history.
    // The backend may have finished processing the request even if the client-side
    // stream failed (network blip, stall timeout, etc.).  If the server did finish,
    // this check will replace the error banner with the actual response automatically.
    onStreamError: (erroredSessionId: string) => {
      console.log('[streamError] Scheduling background history check for session:', erroredSessionId);
      setTimeout(() => {
        // Only reload if we're still on the same session and not already streaming
        if (activeSessionRef.current === erroredSessionId && !isStreamingRef.current) {
          console.log('[streamError] Triggering history reload after stream error');
          setRefetchTrigger(prev => prev + 1);
        }
      }, 4000); // 4 s — gives the backend time to flush its response to storage
    },
    onOrderCreated: (orderId: number) => {
      console.log('[orderCreated] Order created with ID:', orderId);
      setActiveOrderId(orderId);
      // Update the URL to include the new order_id and refresh order details.
      // Set the suppression flag BEFORE navigate() so the URL effect (which runs
      // synchronously on the next render) skips its refetch — otherwise the
      // refetch would wipe the in-flight bubble (HITL prompt / greeting) that
      // arrives in the same turn but hasn't yet been persisted to history.
      const newUrl = `/chat/${activeSessionId}?order_id=${orderId}`;
      suppressNextUrlRefetchRef.current = true;
      navigate(newUrl, { replace: true, state: location.state });
      // Refresh the order object in the background
      refreshAssociatedOrder();
    },
    logAnalyticsEvent: (name: string, params: Record<string, unknown>) =>
      firebaseObject.logAnalyticsEvent(name, params as Record<string, string>),
    // Admin-only part-selection mode. Each order is authoritative: send the
    // order's OWN stored mode; when it has none yet, send undefined so the backend
    // keeps/falls back to the order's value rather than leaking the global default
    // across sessions. The global default is only used pre-order (no order id).
    partSelectionMode: currentUser?.role === 'admin'
      ? ((getOrderObject()?.part_selection_mode as ('best_effort' | 'human_in_the_loop' | undefined))
         || (getOrderObject()?.id ? undefined : partSelectionMode))
      : undefined,
  });

  // Point isStreamingRef at the hook's authoritative ref so all legacy references remain valid.
  // Both variables are MutableRefObjects sharing the same mutable `.current` slot.
  isStreamingRef = streamingRef;

  const sendMessage = async () => {

    const message = inputMessage.trim();
    const hasFiles = selectedFiles.length > 0;
    const hasLegacyImage = selectedImage !== null;

    if ((!message && !hasFiles && !hasLegacyImage) || isLoading) return;

    // Don't send while files are still being pre-uploaded to GCS
    if (selectedFiles.some((f) => f.isUploading)) return;

    // Check if a session is active
    if (!activeSessionId) {
      alert(t('chat.errors.noActiveSession', { defaultValue: 'Please start a chat session before sending messages.' }));
      return;
    }

    // Capture files before clearing UI state
    const filesToSend = [...selectedFiles];
    const imageToSend = selectedImage;
    const imageMimeTypeToSend = selectedImageMimeType;

    // Append hidden parts context if summary items are present
    const partsContextPart = summaryItems.length > 0
      ? `<span style="display:none">PARTS_CONTEXT:${JSON.stringify(summaryItems)}</span>`
      : '';

    // Drain any pending summary-item change / delete annotations accumulated since
    // the last send.  Each entry is already a fully-formed hidden <div> string.
    const pendingAnnotations = pendingSummaryAnnotationsRef.current;
    const annotationsPart = pendingAnnotations.size > 0
      ? Array.from(pendingAnnotations.values()).join('')
      : '';
    // Clear after draining so they aren't repeated on the next send.
    pendingSummaryAnnotationsRef.current = new Map();

    const hiddenSuffix = partsContextPart + annotationsPart;
    const finalMessage = message + hiddenSuffix;

    const userMessage: Message = {
      id: newMessageId('user'),
      text: message,
      vin: null,
      datasetId: null,
      language: currentLanguage,
      sender: 'user',
      timestamp: new Date(),
      uploadedFiles: filesToSend.length > 0 ? filesToSend : undefined,
    };

    setInputMessage('');
    clearAllSelectedFiles();
    clearSelectedImage();

    // If server was still processing in the background (reconnect scenario), cancel the
    // polling indicator — the new send will take over.
    if (backgroundPollingRef.current) {
      clearInterval(backgroundPollingRef.current);
      backgroundPollingRef.current = null;
    }
    setIsBackgroundProcessing(false);

    const filesData = filesToSend.map(f => ({ data: f.data, mimeType: f.mimeType, gcsUri: f.gcsUri }));

    await streamSend({
      message: finalMessage,
      sessionId: activeSessionId,
      language: currentLanguage,
      contextTags,
      filesData: filesData.length > 0
        ? filesData
        : imageToSend
          ? [{ data: imageToSend, mimeType: imageMimeTypeToSend || 'image/jpeg' }]
          : undefined,
      userMessage,
      onBackendTags: setBackendTags,
    });
  };

  // Send message with initial data from HomePage navigation
  const sendMessageWithInitialData = async (message: string, files: Array<{ data: string; mimeType: string; preview: string; name: string; gcsUri?: string }>) => {
    const hasFiles = files.length > 0;

    if ((!message && !hasFiles) || isLoading) return;

    // Check if a session is active
    if (!activeSessionId) {
      alert(t('chat.errors.noActiveSession', { defaultValue: 'Please start a chat session before sending messages.' }));
      return;
    }

    console.log('sendMessageWithInitialData called, current messages count:', messages.length);

    // Automatically add search definitions (tags)
    const newAutoTags: string[] = [];

    // Add query tag if there's a message
    if (message && !userTags.some(tag => tag.startsWith('Query:')) && !backendTags.some(tag => tag.startsWith('Query:'))) {
      // Truncate long queries for display
      const queryPreview = message.length > 50 ? `${message.substring(0, 50)}...` : message;
      if (queryPreview != "NEXT") {
        newAutoTags.push(`Query: ${queryPreview}`);
      }
    }

    // Add images tag if files are being uploaded
    if (hasFiles && !userTags.includes('Files: Attached') && !backendTags.includes('Files: Attached')) {
      const imageCount = files.length;
      // newAutoTags.push(`Files: ${imageCount} attached`);
    }

    // Update user tags with new auto tags
    if (newAutoTags.length > 0) {
      // setUserTags(prev => [...prev, ...newAutoTags]);
    }

    // Capture the session ID at the time of sending to ensure streaming updates go to the correct session
    const streamSessionId = activeSessionId;

    const msgId = newMessageId('user');

    // Mark any audio files as pending transcription so the UI shows "Transcribing…"
    const filesWithPending = files.map(f =>
      f.mimeType.startsWith('audio/') ? { ...f, isTranscribing: true } : f
    );

    const userMessage: Message = {
      id: msgId,
      text: message,
      vin: null,
      datasetId: null,
      language: currentLanguage,
      sender: 'user',
      timestamp: new Date(),
      uploadedFiles: filesWithPending.length > 0 ? filesWithPending : undefined,
    };

    setInputMessage('');
    clearAllSelectedFiles();

    const filesData = files.map(file => ({ data: file.data, mimeType: file.mimeType, gcsUri: file.gcsUri }));

    await streamSend({
      message,
      sessionId: streamSessionId,
      language: currentLanguage,
      contextTags,
      filesData: filesData.length > 0 ? filesData : undefined,
      userMessage,
      onBackendTags: setBackendTags,
    });

    // Transcribe any audio files in the background and patch the message once done
    files.forEach(audioFile => {
      if (!audioFile.mimeType.startsWith('audio/')) return;
      transcribeAudio(audioFile.data, audioFile.mimeType, streamSessionId ?? undefined, currentLanguage)
        .then((transcript) => {
          setMessages(prev => prev.map(m =>
            m.id === msgId
              ? {
                  ...m,
                  uploadedFiles: m.uploadedFiles?.map(f =>
                    f.mimeType.startsWith('audio/')
                      ? { ...f, transcript: transcript ?? undefined, isTranscribing: false }
                      : f
                  ),
                }
              : m
          ));
        })
        .catch(() => {
          setMessages(prev => prev.map(m =>
            m.id === msgId
              ? {
                  ...m,
                  uploadedFiles: m.uploadedFiles?.map(f =>
                    f.mimeType.startsWith('audio/')
                      ? { ...f, isTranscribing: false }
                      : f
                  ),
                }
              : m
          ));
        });
    });
  };

  // Retry message function - memoized to prevent re-renders
  const retryMessage = useCallback(async (message: Message) => {
    // If the message is a bot message, find the previous user message
    let messageToRetry = message;
    if (message.sender === 'bot') {
      const currentIndex = messages.findIndex(m => m.id === message.id);
      const previousUserMessage = [...messages.slice(0, currentIndex)].reverse().find(m => m.sender === 'user');
      if (!previousUserMessage) return;
      messageToRetry = previousUserMessage;
    }

    // Remove error state from the message
    setMessages(prev =>
      prev.map(msg =>
        msg.id === messageToRetry.id
          ? { ...msg, hasError: false, errorDetails: undefined }
          : msg
      )
    );

    // Prevent duplicate sends
    if (isLoading) return;

    // Capture the session ID at the time of sending
    const streamSessionId = activeSessionId;

    // Extract files data from the message - support both multiple files and legacy single image
    let filesToRetry: Array<{ data: string; mimeType: string }> | undefined;

    if (messageToRetry.uploadedFiles && messageToRetry.uploadedFiles.length > 0) {
      // Multiple files format
      filesToRetry = messageToRetry.uploadedFiles.map(f => ({
        data: f.data,
        mimeType: f.mimeType
      }));
    } else if (messageToRetry.uploadedImage && messageToRetry.uploadedImageMimeType) {
      // Legacy single image format
      let imageData = messageToRetry.uploadedImage;
      // Extract base64 data from data URL if present
      if (imageData.startsWith('data:')) {
        imageData = imageData.split(',')[1];
      }
      filesToRetry = [{
        data: imageData,
        mimeType: messageToRetry.uploadedImageMimeType
      }];
    }

    // Retry: no user message injected (the existing user bubble stays in the list)
    // Prefix with SYSTEM_RETRY: so the backend can recognise this as a retry and not display it
    await streamSend({
      message: `SYSTEM_RETRY: ${messageToRetry.text}`,
      sessionId: streamSessionId,
      language: currentLanguage,
      contextTags,
      filesData: filesToRetry,
      userMessage: null,
      onBackendTags: setBackendTags,
    });
  }, [isLoading, activeSessionId, setMessages, contextTags, currentLanguage, setBackendTags, setIsLoading, isSessionActive, streamSend]);

  const clearChat = () => {
    // Remove this session from cache

    setMessages([]); // Clear all messages
    resetTrackedParts();

    // Reset VIN processing flags
    vinProcessedRef.current = false;
    setHasProcessedVin(false);
    setIsProcessingVin(false);

    // Reset context tags
    setContextTags([]);
    setUserTags([]);
    setBackendTags([]);
    setNewTag('');
    setShowAddTag(false);

    // Reset session rating
    setShowSessionRating(false);
    setSessionRating(0);
    setSessionComments('');
    setSessionRatingSubmitted(false);
    setHasSubmittedRating(false);
  };

  const handleResetChat = async () => {
    const currentOrder = getOrderObject();
    if (!activeSessionId || !currentOrder) return;

    // Show confirmation modal
    setShowResetConfirm(true);
  };

  const confirmResetChat = async () => {
    const currentOrder = getOrderObject();
    if (!activeSessionId || !currentOrder) return;

    // Close confirmation modal
    setShowResetConfirm(false);

    try {
      // Call the API to reset the chat - this creates a new session
      const result = await orderService.resetOrderChat(currentOrder.id, currentLanguage);

      console.log('Chat reset successful:', result);

      // Clear current chat state
      clearChat();

      // Update the associated order with the new session ID
      const updatedOrder = { ...currentOrder, session_id: result.new_session_id };

      setOrderObject(updatedOrder);
      setActiveSessionId(result.new_session_id);

      // Reset VIN processing flags BEFORE setting pending VIN
      vinProcessedRef.current = false;
      setHasProcessedVin(false);
      setIsProcessingVin(false);

      // If the order has a VIN, set up for auto-send
      if (updatedOrder.vin) {
        console.log('Reset chat with VIN - setting up auto-send:', updatedOrder.vin);
        pendingVinRef.current = {
          vinNumber: updatedOrder.vin,
          query: '',
          autoSendVin: true
        };
        // Wait for session to be ready before triggering VIN auto-send
        const checkAndTrigger = () => {
          // Check if session is still loading or already processing
          if (isLoadingHistory || isLoading) {
            console.log('Session still loading after reset, retrying VIN auto-send trigger in 200ms');
            setTimeout(checkAndTrigger, 200);
          } else {
            console.log('Session ready after reset, triggering VIN auto-send');
            setVinAutoSendTrigger(prev => prev + 1);
          }
        };
        setTimeout(checkAndTrigger, 100);
      }

      // Show success message
      setShowResetSuccess(true);
    } catch (error) {
      console.error('Error resetting chat:', error);
      setShowResetError(true);
    }
  };

  const requestAssistance = useCallback(() => {
    const currentOrder = getOrderObject();
    if (!activeSessionId || !currentOrder) return;
    setShowAssistanceModal(true);
  }, [activeSessionId, getOrderObject]);

  const handleAssistanceSuccess = () => {
    // Optionally refresh orders or show a notification
  };

  // Claim assignment without changing the order/session identity owner (`uid`).
  const handleTakeOwnership = useCallback(async () => {
    const currentOrder = getOrderObject();
    if (!currentOrder) return;
    try {
      const updatedOrder = await orderService.lifecycleTake(currentOrder.id);
      // Update local state: order now belongs to current user → clear read-only flag
      setOrderObject(updatedOrder);
      setActiveOrderId(updatedOrder.id);
      // Trigger a session history refresh so the chat becomes editable immediately
      setRefetchTrigger(prev => prev + 1);
    } catch (error: any) {
      console.error('Failed to take ownership:', error);
      const detail = error?.response?.data?.detail;
      alert(
        typeof detail === 'string' && detail
          ? detail
          : t('orders.owner.takeFailed', { defaultValue: 'Could not take this order. Please refresh and try again.' })
      );
      // Someone else may have won the race — reload so the banner names the actual owner.
      setRefetchTrigger(prev => prev + 1);
    }
  }, [getOrderObject, setOrderObject, setActiveOrderId, t]);

  // Memoized callbacks for ChatInput
  const handleShowMobileOrders = useCallback(() => setShowMobileOrders(true), []);
  const handleShowMobileCart = useCallback(() => setShowMobileCart(true), []);
  const handleShowSessionRating = useCallback(() => setShowSessionRating(true), []);

  const handleSummaryItemChange = useCallback((index: number, value: string) => {
    // Capture the original value BEFORE the state update so we can build the annotation.
    const oldValue = summaryItemsRef.current[index];
    setSummaryItems(prev => prev.map((it, i) => i === index ? value : it));

    // Record a single "update" annotation per original item (last-write-wins).
    // The key is the original text so repeated edits of the same item collapse.
    if (oldValue !== undefined && oldValue !== value) {
      const key = `__update__:${oldValue}`;
      pendingSummaryAnnotationsRef.current.set(
        key,
        `<div style="display:none"> update user request from ${oldValue} to ${value} </div>`
      );
      // If there was a pending delete for this same original item, remove it —
      // the user changed their mind and edited instead.
      pendingSummaryAnnotationsRef.current.delete(`__delete__:${oldValue}`);
    }
  }, []);

  const handleSummaryItemDelete = useCallback((index: number) => {
    // Capture the item being deleted BEFORE the state update.
    const deletedItem = summaryItemsRef.current[index];
    setSummaryItems(prev => prev.filter((_, i) => i !== index));

    if (deletedItem !== undefined) {
      const deleteKey = `__delete__:${deletedItem}`;
      pendingSummaryAnnotationsRef.current.set(
        deleteKey,
        `<div style="display:none"> user request to delete ${deletedItem} </div>`
      );
      // If there was a pending update for this item, remove it — delete supersedes.
      pendingSummaryAnnotationsRef.current.delete(`__update__:${deletedItem}`);
    }
  }, []);

  const handleSummaryItems = useCallback((items: string[]) => {
    setSummaryItems(items);
  }, []);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isLoading) return;
      sendMessage();
    }
  }, [sendMessage, isLoading]);

  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  const toggleErrorDetails = useCallback((messageId: string) => {
    setExpandedErrors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  }, []);

  const handle_new_order_created = (newOrder: OrderResponse) => {
    // Clear messages and reset state
    setMessages([]);
    setContextTags([]);
    setUserTags([]);
    setBackendTags([]);
    setNewTag('');
    setShowAddTag(false);
    setShowSessionRating(false);
    setSessionRating(0);
    setSessionComments('');
    setSessionRatingSubmitted(false);
    setHasSubmittedRating(false);


    // Set up new order and session state
    setOrderObject(newOrder);
    setActiveSessionId(newOrder.session_id);

    // Add the session to the sessions list
    setActiveSessionId(newOrder.session_id);

    // Update URL to use session ID
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('session', newOrder.session_id);
    navigate(`/chat?${newSearchParams.toString()}`, { replace: true });

    // Reset VIN processing flags BEFORE setting pending VIN
    vinProcessedRef.current = false;
    setHasProcessedVin(false);
    setIsProcessingVin(false);
    setIsLoading(false); // Ensure loading is false

    // Set pending VIN if available
    if (newOrder.vin) {
      console.log('Setting pending VIN for auto-send:', newOrder.vin);
      pendingVinRef.current = {
        vinNumber: newOrder.vin,
        query: '',
        autoSendVin: true
      };

      // Wait for session to be ready before triggering VIN auto-send
      const checkAndTrigger = () => {
        // Check if session is still loading or already processing
        if (isLoadingHistory || isLoading) {
          console.log('Session still loading for new order, retrying VIN auto-send trigger in 200ms');
          setTimeout(checkAndTrigger, 200);
        } else {
          console.log('Triggering VIN auto-send for new order (after state flush)');
          console.log('Current pendingVinRef:', pendingVinRef.current);
          console.log('Current vinProcessedRef:', vinProcessedRef.current);
          setVinAutoSendTrigger(prev => prev + 1);
        }
      };

      // Use requestAnimationFrame to ensure React has flushed all state updates
      requestAnimationFrame(() => {
        setTimeout(checkAndTrigger, 100);
      });
    } else {
      console.log('No VIN provided for new order');
      pendingVinRef.current = null;
    }

    // Update draft orders flag
    setHasDraftOrders(true);

    // Focus on input
    // setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleOrdersUpdate = (ordersCount: number) => {
    setHasDraftOrders(ordersCount > 0);
  };

  // Helper function to check if a session has new messages
  // Simplified: without session cache, we can't track background session updates
  const hasNewMessagesForSession = (sessionId: string): boolean => {
    // Active session never shows as having new messages
    return false;
  };

  // Backward compatibility: check if an order has new messages (via its session)
  // Simplified: without session cache, we can't track background order updates
  const hasNewMessagesForOrder = (orderId: number): boolean => {
    return false;
  };

  const copyErrorDetails = useCallback(async (errorDetails: any, messageId: string) => {
    const errorInfo = {
      message: errorDetails.message,
      type: errorDetails.name,
      status: errorDetails.status,
      timestamp: messages.find(m => m.id === messageId)?.timestamp?.toISOString(),
      stack: errorDetails.stack,
      serverResponse: errorDetails.response_text,
      cause: errorDetails.cause,
    };

    const errorText = `${t('chat.errorDetails.title', { defaultValue: 'Error Details' })}:
${t('chat.errorDetails.message', { defaultValue: 'Message' })}: ${errorInfo.message}
${t('chat.errorDetails.type', { defaultValue: 'Type' })}: ${errorInfo.type || t('chat.errorDetails.unknown', { defaultValue: 'Unknown' })}
${errorInfo.status ? `${t('chat.errorDetails.httpStatus', { defaultValue: 'HTTP Status' })}: ${errorInfo.status}` : ''}
${t('chat.errorDetails.timestamp', { defaultValue: 'Timestamp' })}: ${errorInfo.timestamp}
${errorInfo.serverResponse ? `\n${t('chat.errorDetails.serverResponse', { defaultValue: 'Server Response' })}:\n${errorInfo.serverResponse}` : ''}
${errorInfo.stack ? `\n${t('chat.errorDetails.stackTrace', { defaultValue: 'Stack Trace' })}:\n${errorInfo.stack}` : ''}
${errorInfo.cause ? `\n${t('chat.errorDetails.cause', { defaultValue: 'Cause' })}:\n${JSON.stringify(errorInfo.cause, null, 2)}` : ''}`;

    try {
      await navigator.clipboard.writeText(errorText);
      // You could add a toast notification here if you have one
    } catch (err) {
      console.error('Failed to copy error details:', err);
    }
  }, [messages, t]);

  const addContextTag = (tag: string) => {
    if (tag.trim() && !userTags.includes(tag.trim()) && !backendTags.includes(tag.trim())) {
      // setUserTags(prev => [...prev, tag.trim()]);
    }
    setNewTag('');
    setShowAddTag(false);
  };

  const removeContextTag = (tagToRemove: string) => {
    // Check if it's a user tag first, then backend tag
    if (userTags.includes(tagToRemove)) {
      // setUserTags(prev => prev.filter(tag => tag !== tagToRemove));
    } else if (backendTags.includes(tagToRemove)) {
      // setBackendTags(prev => prev.filter(tag => tag !== tagToRemove));
    }
  };

  // Add vision analysis part to cart
  // Add vision part to cart - memoized to prevent re-renders
  const addVisionPartToCart = useCallback(async (part: any, messageStructuredData: any) => {
    if (!getActiveOrderId()) return;

    const partKey = part.partId || part.partNumber || `${part.partName}-${Date.now()}`;

    // Prevent double-clicks
    if (addingToCartItems.has(partKey)) return;

    setAddingToCartItems(prev => new Set(prev).add(partKey));

    try {
      // Get VIN data from message structured data if available
      const vinData = messageStructuredData?.vin_data ?? messageStructuredData?.VIN_DATA ?? {
        make: '',
        manufacturer: '',
        model: '',
        trim: '',
        vin: '',
        year: ''
      };

      // Original search term for this part — persisted inside part_data so the
      // cart "search" badge survives reloads (the top-level query field below is
      // not part of the durable order schema).
      const relatedQuery = messageStructuredData?.part_data?.query || part.related_user_query || null;

      const structuredData = {
        vin_data: vinData,
        part_data: {
          part_id: String(part.partId ?? part.partNumber ?? partKey),
          part_number: String(part.partNumber ?? t('common.notAvailable', { defaultValue: 'N/A' })),
          description: String(part.description ?? part.partName ?? t('chat.visionAnalysis.identifiedPart', { defaultValue: 'Vision identified part' })),
          price: String(part.price ?? t('common.notAvailable', { defaultValue: 'N/A' })),
          not_found: part.not_found || false,
          ...(relatedQuery ? { query: String(relatedQuery) } : {}),
          // Datasource (vector-search suggestion) lines: the price comes from
          // the data-source row itself — the marker makes the backend skip the
          // DMS pricing DAG and the cart render a fixed supplier price.
          ...(part.price_source ? { price_source: String(part.price_source) } : {}),
          ...(part.supplier_name ? { supplier_name: String(part.supplier_name) } : {}),
          ...(part.currency ? { currency: String(part.currency) } : {}),
        },
        selection_reason: t('chat.visionAnalysis.selectionReason', {
          defaultValue: 'Identified from image analysis{{confidence}}',
          confidence: part.probability_score ? ` (${Math.round(part.probability_score * 100)}% ${t('chat.visionAnalysis.confidence', { defaultValue: 'confidence' })})` : ''
        })
      };

      const cartItem = {
        part_id: String(part.partId ?? part.partNumber ?? partKey),
        structured_data: structuredData,
        image: part.image || null,
        // Search title = the user's ORIGINAL term (parenthetical). Mirrors the
        // durable part_data.query above; || (not ??) so an empty string falls
        // back instead of producing a blank search badge in the cart.
        query: relatedQuery,
        is_service: part.is_service || false,
        translated_description: part.translated_description || null,
        // Pre-priced lines (datasource suggestions) attach their pricing_data at
        // add time: the line renders priced immediately, the cache prefetch skips
        // it, and the pricing DAG never runs for it.
        ...(Array.isArray(part.pricing_data) ? { pricing_data: part.pricing_data } : {}),
      };

      // Use the central service to add item.
      // Pass current order items from memory so addItemToOrder skips the GET round-trip.
      // Seed the new line with the catalog-recommended quantity (part.quantity).
      const currentOrderItems = getOrderObject()?.parts_in_order?.items ?? [];
      const catalogQty = parseCatalogQuantity((part as any)?.quantity);
      const result = await cartOrderService.addItem(cartItem, getActiveOrderId()!, cartItems as any, currentOrderItems, catalogQty);

      // Use the order returned by the PUT instead of issuing a redundant GET.
      const updatedOrder = result.updatedOrder ?? await orderService.getOrderById(getActiveOrderId()!);
      setOrderObject(updatedOrder);

      firebaseObject.logAnalyticsEvent('vision_part_added_to_cart', {
        part_number: cartItem.structured_data?.part_data?.part_number ?? part.partNumber,
        part_name: cartItem.structured_data?.part_data?.description ?? part.partName,
        probability_score: part.probability_score,
        order_id: getActiveOrderId(),
        added_to_order: result.addedToOrder
      });
    } catch (error) {
      console.error('Error adding vision part to cart:', error);
    } finally {
      setAddingToCartItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(partKey);
        return newSet;
      });
    }
  }, [getActiveOrderId, setAddingToCartItems, setOrderObject, setMessages, activeSessionId]);

  // Feedback handlers - memoized to prevent re-renders
  const handleMessageFeedback = useCallback(async (messageId: string, helpful: boolean) => {
    try {
      const message = messages.find(m => m.id === messageId);
      if (!message || message.sender !== 'bot') return;

      // Update UI immediately
      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? { ...msg, feedback: { ...msg.feedback, helpful } }
            : msg
        )
      );

      // Submit feedback to backend
      await FeedbackService.rateMessage({
        session_id: activeSessionId,
        message_id: messageId,
        rating: helpful ? 5 : 1,
        helpful: helpful,
      });

      firebaseObject.logAnalyticsEvent('message_feedback', {
        message_id: messageId,
        helpful: helpful,
        session_id: activeSessionId,
      });
    } catch (error) {
      console.error('Error submitting message feedback:', error);
      // Optionally revert UI change on error
    }
  }, [messages, setMessages, activeSessionId]);

  const handleSessionRating = async () => {
    if (sessionRating === 0) return;

    try {
      await FeedbackService.rateSession({
        session_id: activeSessionId,
        rating: sessionRating,
        comments: sessionComments || undefined,
      });

      setSessionRatingSubmitted(true);
      setHasSubmittedRating(true);
      firebaseObject.logAnalyticsEvent('session_feedback', {
        session_id: activeSessionId,
        rating: sessionRating,
        has_comments: !!sessionComments,
      });

      // Close the rating dialog after a short delay
      setTimeout(() => {
        setShowSessionRating(false);
        setSessionRatingSubmitted(false);
      }, 1500);
    } catch (error) {
      console.error('Error submitting session rating:', error);
    }
  };

  const handleRemovePart = async (item: any) => {
    const currentOrder = getOrderObject();
    if (!currentOrder) return;

    const partId = item.structured_data?.part_data?.part_id || item.structured_data?.PART_DATA?.PART_ID;
    if (!partId) return;

    // No optimistic update — item disappears only after the backend confirms removal.
    try {
      const updatedOrder = await cartOrderService.removeItemFromOrder(partId, currentOrder.id);

      setOrderObject(updatedOrder);
      setOrderRefreshTrigger((prev) => prev + 1);
    } catch (e) {
      console.error('Error removing part:', e);
      refreshAssociatedOrder();
    }
  };

  const handleUpdatePartQuantity = async (item: any, newQuantity: number) => {
    const currentOrder = getOrderObject();
    if (!currentOrder) return;
    if (newQuantity <= 0) return;

    const partId = item.structured_data?.part_data?.part_id || item.structured_data?.PART_DATA?.PART_ID;
    if (!partId) return;

    // Optimistic UI update
    const existingItems = currentOrder.parts_in_order?.items || [];
    const updatedItems = existingItems.map((orderItem: any) => {
      const id = orderItem.structured_data?.part_data?.part_id || orderItem.structured_data?.PART_DATA?.PART_ID;
      return id === partId ? { ...orderItem, quantity: newQuantity } : orderItem;
    });
    const total = updatedItems.reduce((sum: number, it: any) => sum + (Number(it.quantity) || 1), 0);
    const optimisticOrder = {
      ...currentOrder,
      parts_in_order: {
        ...(currentOrder.parts_in_order || {}),
        items: updatedItems,
        total_items: total,
      },
    } as any;
    setOrderObject(optimisticOrder);

    try {
      const updatedOrder = await cartOrderService.updateItemQuantityInOrder(partId, newQuantity, currentOrder.id);

      setOrderObject(updatedOrder);
      setOrderRefreshTrigger((prev) => prev + 1);
    } catch (e) {
      console.error('Error updating quantity:', e);
      refreshAssociatedOrder();
    }
  };

  // Handle manual price update from CartSidePanel's inline price editor
  const handlePriceUpdate = async (
    item: any,
    newPrice: number | null,
    selection?: { dms_type: string | null; dms_location: string | null },
  ) => {
    const currentOrder = getOrderObject();
    if (!currentOrder) return;

    // Use the same key logic as CartSidePanel: item.id > item.part_id > part_number
    const getItemKeyForPrice = (i: any) => {
      const pd = i.structured_data?.part_data || i.structured_data?.PART_DATA || {};
      return i.id || i.part_id || pd.part_number || pd.PART_NUMBER || null;
    };
    const itemKey = getItemKeyForPrice(item);
    const existingItems: any[] = currentOrder.parts_in_order?.items || [];
    const updatedItems = existingItems.map((orderItem: any) => {
      const id = getItemKeyForPrice(orderItem);
      if (id !== itemKey) return orderItem;
      // Patch the price on the item and mark it as manually set
      return {
        ...orderItem,
        price: newPrice,
        structured_data: {
          ...orderItem.structured_data,
          part_data: {
            ...(orderItem.structured_data?.part_data || {}),
            price: newPrice,
            // Where the price came from (DMS line-type + warehouse / point of
            // sale), so the cart re-opens on the same choice after a reload.
            ...(selection ? { dms_type: selection.dms_type, dms_location: selection.dms_location } : {}),
          },
        },
      };
    });

    // Optimistic update
    setOrderObject({ ...currentOrder, parts_in_order: { ...currentOrder.parts_in_order, items: updatedItems } } as any);

    try {
      const updatedOrder = await orderService.updateOrder(currentOrder.id, {
        parts_in_order: {
          items: updatedItems,
          total_items: updatedItems.reduce((s: number, i: any) => s + (i.quantity || 1), 0),
          export_timestamp: new Date().toISOString(),
          vins: Array.from(new Set(updatedItems.map((i: any) => i.structured_data?.vin_data?.vin).filter(Boolean))),
        },
      });
      setOrderObject(updatedOrder);
    } catch (e) {
      console.error('Error saving manual price:', e);
      refreshAssociatedOrder();
    }
  };

  // Persist a manual per-line delivery-time choice (spec §6) onto the order line.
  // Mirrors handlePriceUpdate: patch parts_in_order.items[].structured_data.part_data
  // .delivery_days, optimistic-update, then save. null resets the line to auto.
  const handleDeliveryUpdate = async (item: any, days: number | null) => {
    const currentOrder = getOrderObject();
    if (!currentOrder) return;
    const getItemKey = (i: any) => {
      const pd = i.structured_data?.part_data || i.structured_data?.PART_DATA || {};
      return i.id || i.part_id || pd.part_number || pd.PART_NUMBER || null;
    };
    const itemKey = getItemKey(item);
    const existingItems: any[] = currentOrder.parts_in_order?.items || [];
    const updatedItems = existingItems.map((orderItem: any) => {
      if (getItemKey(orderItem) !== itemKey) return orderItem;
      return {
        ...orderItem,
        structured_data: {
          ...orderItem.structured_data,
          part_data: {
            ...(orderItem.structured_data?.part_data || {}),
            delivery_days: days,
          },
        },
      };
    });

    // Optimistic update
    setOrderObject({ ...currentOrder, parts_in_order: { ...currentOrder.parts_in_order, items: updatedItems } } as any);

    try {
      const updatedOrder = await orderService.updateOrder(currentOrder.id, {
        parts_in_order: {
          items: updatedItems,
          total_items: updatedItems.reduce((s: number, i: any) => s + (i.quantity || 1), 0),
          export_timestamp: new Date().toISOString(),
          vins: Array.from(new Set(updatedItems.map((i: any) => i.structured_data?.vin_data?.vin).filter(Boolean))),
        },
      });
      setOrderObject(updatedOrder);
    } catch (e) {
      console.error('Error saving delivery time:', e);
      refreshAssociatedOrder();
    }
  };

  // Persist a per-line free-text note (structured_data.part_data.note). The key
  // stays present with null on clear so applyServerOrder's local-wins merge can
  // tell "cleared" apart from "never set".
  const handleNoteUpdate = async (item: any, note: string | null) => {
    const currentOrder = getOrderObject();
    if (!currentOrder) return;
    const getItemKey = (i: any) => {
      const pd = i.structured_data?.part_data || i.structured_data?.PART_DATA || {};
      return i.id || i.part_id || pd.part_number || pd.PART_NUMBER || null;
    };
    const itemKey = getItemKey(item);
    const existingItems: any[] = currentOrder.parts_in_order?.items || [];
    const updatedItems = existingItems.map((orderItem: any) => {
      if (getItemKey(orderItem) !== itemKey) return orderItem;
      return {
        ...orderItem,
        structured_data: {
          ...orderItem.structured_data,
          part_data: {
            ...(orderItem.structured_data?.part_data || {}),
            note,
          },
        },
      };
    });

    // Optimistic update
    setOrderObject({ ...currentOrder, parts_in_order: { ...currentOrder.parts_in_order, items: updatedItems } } as any);

    try {
      const updatedOrder = await orderService.updateOrder(currentOrder.id, {
        parts_in_order: {
          items: updatedItems,
          total_items: updatedItems.reduce((s: number, i: any) => s + (i.quantity || 1), 0),
          export_timestamp: new Date().toISOString(),
          vins: Array.from(new Set(updatedItems.map((i: any) => i.structured_data?.vin_data?.vin).filter(Boolean))),
        },
      });
      setOrderObject(updatedOrder);
    } catch (e) {
      console.error('Error saving part note:', e);
      refreshAssociatedOrder();
    }
  };

  // Append a workflow audit note — fire-and-forget
  const handleWorkflowLog = (note: string, stepType: 'edit_price' | 'edit_discount' | 'edit_description') => {
    const currentOrder = getOrderObject();
    if (!currentOrder) return;
    orderService.addWorkflowNote(currentOrder.id, note, stepType).catch(() => {/* non-critical */});
  };

  // Merge a fresh server order into local state, preserving local
  // translated_description / description / manually-saved price so a refresh or
  // re-check never clobbers edits the user just made. Fresh price + availability
  // (integration_data) flow through from the server item. Shared by the polling
  // refresh and the per-line "Re-check" handler.
  const applyServerOrder = (updated: OrderResponse) => {
    const currentOrder = getOrderObject();
    if (!currentOrder) { setOrderObject(updated); return; }
    const currentItems: any[] = currentOrder.parts_in_order?.items || [];
    const refreshedItems: any[] = updated.parts_in_order?.items || [];
    const getKey = (i: any) => {
      const pd = i.structured_data?.part_data || i.structured_data?.PART_DATA || {};
      return i.id || i.part_id || pd.part_number || null;
    };
    const mergedItems = refreshedItems.map((ri: any) => {
      const key = getKey(ri);
      const local = key !== null ? currentItems.find((ci: any) => getKey(ci) === key) : null;
      if (!local) return ri;
      const translated_description = local.translated_description || ri.translated_description;
      const localPartData = local.structured_data?.part_data || {};
      const riPartData = ri.structured_data?.part_data || {};
      const part_data = {
        ...riPartData,
        description: localPartData.description || riPartData.description,
        ...(localPartData.price != null ? { price: localPartData.price } : {}),
        // Local note wins (incl. an explicit null = just cleared) so a refresh
        // mid-edit never reverts it.
        ...('note' in localPartData ? { note: localPartData.note } : {}),
      };
      return {
        ...ri,
        ...(local.price != null ? { price: local.price } : {}),
        ...(translated_description ? { translated_description } : {}),
        structured_data: { ...ri.structured_data, part_data },
      };
    });
    setOrderObject({
      ...updated,
      parts_in_order: { ...updated.parts_in_order, items: mergedItems },
    });
  };

  // Re-check one cart line's price/availability — re-runs the add-to-cart
  // (item_added_to_cart) workflow server-side, then merges the fresh order in.
  const handleRecheckInventory = async (item: any) => {
    const orderId = getActiveOrderId();
    if (!orderId) return;
    const pd = item?.structured_data?.part_data || item?.structured_data?.PART_DATA || {};
    const itemId = item?.id || item?.part_id || pd.part_id || pd.part_number;
    if (!itemId) return;
    const updated = await orderService.recheckCartItem(orderId, String(itemId));
    applyServerOrder(updated);
  };

  // Handle description update from CartSidePanel inline editor
  const handleDescriptionUpdate = async (item: any, newDescription: string) => {
    const currentOrder = getOrderObject();
    if (!currentOrder) return;
    // Use the same key logic as CartSidePanel: item.id > item.part_id > part_number
    const getItemKey = (i: any) => {
      const pd = i.structured_data?.part_data || i.structured_data?.PART_DATA || {};
      return i.id || i.part_id || pd.part_number || pd.PART_NUMBER || null;
    };
    const itemKey = getItemKey(item);
    const existingItems: any[] = currentOrder.parts_in_order?.items || [];
    const updatedItems = existingItems.map((orderItem: any) => {
      if (itemKey !== null && getItemKey(orderItem) !== itemKey) return orderItem;
      // Fallback: match by index if keys are all null (shouldn't happen but safe)
      if (itemKey === null && orderItem !== item) return orderItem;
      return {
        ...orderItem,
        translated_description: newDescription,
        structured_data: {
          ...orderItem.structured_data,
          part_data: {
            ...(orderItem.structured_data?.part_data || {}),
            description: newDescription,
          },
        },
      };
    });

    setOrderObject({ ...currentOrder, parts_in_order: { ...currentOrder.parts_in_order, items: updatedItems } } as any);

    console.log('[descriptionUpdate] Sending PUT — item key:', itemKey, 'newDescription:', newDescription,
      'updatedItem translated_description:',
      updatedItems.find((i: any) => getItemKey(i) === itemKey)?.translated_description
    );

    try {
      const updatedOrder = await orderService.updateOrder(currentOrder.id, {
        parts_in_order: {
          items: updatedItems,
          total_items: updatedItems.reduce((s: number, i: any) => s + (i.quantity || 1), 0),
          export_timestamp: new Date().toISOString(),
          vins: Array.from(new Set(updatedItems.map((i: any) => i.structured_data?.vin_data?.vin).filter(Boolean))),
        },
      });
      console.log('[descriptionUpdate] PUT success — response item translated_descriptions:',
        updatedOrder.parts_in_order?.items?.map((i: any) => ({ key: i.id || i.part_id, translated_description: i.translated_description }))
      );
      setOrderObject(updatedOrder);
    } catch (e) {
      console.error('Error saving description:', e);
      refreshAssociatedOrder();
    }
  };

  // Handle per-line manual discount from CartSidePanel's inline discount editor
  const handleLineDiscountUpdate = async (item: any, value: number | null, type: 'percentage' | 'fixed') => {
    const currentOrder = getOrderObject();
    if (!currentOrder) return;

    const getItemKey = (i: any) => {
      const pd = i.structured_data?.part_data || i.structured_data?.PART_DATA || {};
      return i.id || i.part_id || pd.part_number || pd.PART_NUMBER || null;
    };
    const itemKey = getItemKey(item);
    const existingItems: any[] = currentOrder.parts_in_order?.items || [];
    const updatedItems = existingItems.map((orderItem: any) => {
      if (getItemKey(orderItem) !== itemKey) return orderItem;
      const partData = orderItem.structured_data?.part_data || {};
      const updatedPartData = value !== null && value > 0
        ? { ...partData, line_discount: { value, type } }
        : (({ line_discount: _, ...rest }) => rest)(partData);
      return {
        ...orderItem,
        structured_data: {
          ...orderItem.structured_data,
          part_data: updatedPartData,
        },
      };
    });

    // Optimistic update
    setOrderObject({ ...currentOrder, parts_in_order: { ...currentOrder.parts_in_order, items: updatedItems } } as any);

    try {
      const updatedOrder = await orderService.updateOrder(currentOrder.id, {
        parts_in_order: {
          items: updatedItems,
          total_items: updatedItems.reduce((s: number, i: any) => s + (i.quantity || 1), 0),
          export_timestamp: new Date().toISOString(),
          vins: Array.from(new Set(updatedItems.map((i: any) => i.structured_data?.vin_data?.vin).filter(Boolean))),
        },
      });
      setOrderObject(updatedOrder);
    } catch (e) {
      console.error('Error saving line discount:', e);
      refreshAssociatedOrder();
    }
  };

  // Handle manual labor estimate update from CartSidePanel's LaborEstimateModal
  const handleLaborUpdate = async (item: any, estimate: import('../services/CartOrderService').RepairEstimate | null) => {
    const currentOrder = getOrderObject();
    if (!currentOrder) return;

    const updatedItem = repairEstimationService.setLaborEstimateOnItem(item, estimate);
    const existingItems: any[] = currentOrder.parts_in_order?.items || [];
    const partId = item.structured_data?.part_data?.part_id || item.structured_data?.PART_DATA?.PART_ID || item.part_id;
    const updatedItems = existingItems.map((orderItem: any) => {
      const id = orderItem.structured_data?.part_data?.part_id || orderItem.structured_data?.PART_DATA?.PART_ID || orderItem.part_id;
      return id === partId ? updatedItem : orderItem;
    });

    // Optimistic update
    setOrderObject({ ...currentOrder, parts_in_order: { ...currentOrder.parts_in_order, items: updatedItems } } as any);

    try {
      await orderService.updateOrder(currentOrder.id, {
        parts_in_order: {
          items: updatedItems,
          total_items: updatedItems.reduce((s: number, i: any) => s + (i.quantity || 1), 0),
          export_timestamp: new Date().toISOString(),
          vins: Array.from(new Set(updatedItems.map((i: any) => i.structured_data?.vin_data?.vin).filter(Boolean))),
        },
      });
    } catch (e) {
      console.error('Error saving labor estimate:', e);
      refreshAssociatedOrder();
    }
  };

  // Handle AI repair estimation — calls Gemini client-side, updates items + adds a service item
  const handleEstimateRepairs = async (currentCartItems: any[]) => {
    const currentOrder = getOrderObject();
    if (!currentOrder) return;

    const { updatedCartItems, serviceItems } = await repairEstimationService.estimateRepairsForCart(
      currentCartItems,
      currentOrder.vin_data,
      85,
      currentLanguage,
      {
        selectionReason: t('cart.sidePanel.estimation.selectionReason', { defaultValue: 'AI-generated repair labor estimation' }),
        repairTypeLabels: {
          repair:   t('cart.sidePanel.labor.repairTypes.repair',   { defaultValue: 'Repair'   }),
          replace:  t('cart.sidePanel.labor.repairTypes.replace',  { defaultValue: 'Replace'  }),
          paint:    t('cart.sidePanel.labor.repairTypes.paint',    { defaultValue: 'Paint'    }),
          diagnose: t('cart.sidePanel.labor.repairTypes.diagnose', { defaultValue: 'Diagnose' }),
          other:    t('cart.sidePanel.labor.repairTypes.other',    { defaultValue: 'Other'    }),
        },
      }
    );

    // Remove any previously generated estimation service items, then append new ones
    const withoutOldServices = updatedCartItems.filter(
      (i: any) => !(i.is_service && (i.part_id || '').startsWith('repair-estimation-'))
    );
    const finalItems = [...withoutOldServices, ...serviceItems];

    const total = finalItems.reduce((s: number, i: any) => s + (i.quantity || 1), 0);
    const vins = Array.from(new Set(finalItems.map((i: any) => i.structured_data?.vin_data?.vin).filter(Boolean)));

    const updatedOrder = await orderService.updateOrder(currentOrder.id, {
      parts_in_order: {
        items: finalItems,
        total_items: total,
        export_timestamp: new Date().toISOString(),
        vins,
      },
    });

    setOrderObject(updatedOrder);
  };

  // Handle Magic Edit - sends a message to the chat session to replace a part
  const handleMagicEdit = async (item: any, prompt: string) => {    // Prevent duplicate sends
    if (isLoading) return;

    // Check if a session is active
    if (!activeSessionId) {
      alert(t('chat.errors.noActiveOrder', { defaultValue: 'Please select or create an order before starting a chat.' }));
      return;
    }

    const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
    const partId = partData.part_id || partData.PART_ID || 'Unknown';
    const description = partData.description || partData.DESCRIPTION || '';

    const message = `Replace part ${partId} (${description}) with: ${prompt}`;

    const userMessage: Message = {
      id: newMessageId('user'),
      text: message,
      vin: null,
      datasetId: null,
      language: currentLanguage,
      sender: 'user',
      timestamp: new Date(),
    };

    setInputMessage('');

    await streamSend({
      message,
      sessionId: activeSessionId,
      language: currentLanguage,
      contextTags: [...userTags, ...backendTags],
      filesData: undefined,
      userMessage,
      onBackendTags: setBackendTags,
    });
  };

  return (
    <AppLayout
      sidebarCallbacks={{
        onNewOrderCreated: handle_new_order_created,
        onClearChat: clearChat,
        onOrdersUpdate: handleOrdersUpdate,
        hasNewMessagesForOrder: hasNewMessagesForOrder,
      }}
    >
      <MobileHeader title={t('header.chat', { defaultValue: 'Chat' })} />
      <div className="flex-1 flex flex-col bg-main-bg overflow-hidden">
        {/* Reset Chat Confirmation Modal */}
        <ConfirmationModal
          isOpen={showResetConfirm}
          title={t('chat.resetChatTitle', { defaultValue: 'Reset Chat Session' })}
          message={t('chat.confirmResetChat', {
            defaultValue: 'Are you sure you want to reset this chat? The current conversation will be archived and a new chat session will begin.'
          })}
          confirmText={t('chat.resetChatConfirm', { defaultValue: 'Reset Chat' })}
          cancelText={t('common.cancel', { defaultValue: 'Cancel' })}
          onConfirm={confirmResetChat}
          onCancel={() => setShowResetConfirm(false)}
          type="warning"
        />

        {/* Reset Chat Success Modal */}
        <ConfirmationModal
          isOpen={showResetSuccess}
          title={t('chat.resetChatSuccessTitle', { defaultValue: 'Chat Reset Successful' })}
          message={t('chat.chatResetSuccess', {
            defaultValue: 'Chat has been reset successfully. You can start a new conversation.'
          })}
          confirmText={t('common.ok', { defaultValue: 'OK' })}
          cancelText=""
          onConfirm={() => setShowResetSuccess(false)}
          onCancel={() => setShowResetSuccess(false)}
          type="success"
        />

        {/* Reset Chat Error Modal */}
        <ConfirmationModal
          isOpen={showResetError}
          title={t('chat.resetChatErrorTitle', { defaultValue: 'Reset Failed' })}
          message={t('chat.chatResetError', {
            defaultValue: 'Failed to reset chat. Please try again.'
          })}
          confirmText={t('common.tryAgain', { defaultValue: 'Try Again' })}
          cancelText={t('common.cancel', { defaultValue: 'Cancel' })}
          onConfirm={() => {
            setShowResetError(false);
            setShowResetConfirm(true);
          }}
          onCancel={() => setShowResetError(false)}
          type="danger"
        />

        {/* Session Rating Modal */}
        <SessionRatingModal
          isOpen={showSessionRating}
          sessionRating={sessionRating}
          sessionComments={sessionComments}
          sessionRatingSubmitted={sessionRatingSubmitted}
          hasSubmittedRating={hasSubmittedRating}
          onClose={() => {
            setShowSessionRating(false);
            setSessionRatingSubmitted(false);
          }}
          onRatingChange={setSessionRating}
          onCommentsChange={setSessionComments}
          onSubmit={handleSessionRating}
        />
        <LockedOrderContext.Provider value={lockedOrderPrompt}>
          {/* Chat Header - VIN Information from associated order */}
          {<ChatHeader activeOrder={getOrderObject()} messages={messages} onOrderUpdated={(updated) => setOrderObject(updated)} isReadOnly={isReadOnly} ownerName={orderOwnerName} ownerIsMe={orderOwnedByMe} stripShown={!!activeLifecycleOrder?.attention_flag && !isSharedView} onTakeOwnership={canTakeOrder ? handleTakeOwnership : undefined} externalEnrichment={externalEnrichment} onAttachCaseFile={isReadOnly ? undefined : attachRemoteFile}
            mobileAction={totalItems > 0 && !showMobileCart && !isReadOnly ? (
              <button
                onClick={() => setShowMobileCart(true)}
                aria-label={t('cart.title', { defaultValue: 'Cart' })}
                className="relative w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-sm active:scale-95 transition-transform"
              >
                <ShoppingCart className="w-4 h-4" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                  {totalItems}
                </span>
              </button>
            ) : undefined}
          />}

          {/* Attention strip — same reason + inline fix as the orders list (lifecycle spec §4) */}
          {activeLifecycleOrder?.attention_flag && !isSharedView && (
            <OrderAttentionStrip
              order={activeLifecycleOrder}
              onUpdated={(updated) => { setOrderObject(updated); setRefetchTrigger(prev => prev + 1); }}
              onOpen={() => undefined}
              currentUid={currentUser?.uid}
              inOrder
            />
          )}

          {/* Context Tags Bar - Minimizable on mobile */}
          {/* <div className="border-b border-gray-100 bg-white">
            <ContextTagsBar
              contextTags={backendTags}
            />
          </div> */}

          {/* Messages */}
          <div className={`flex-1 overflow-hidden ${!isReadOnly ? 'chat-messages' : ''} w-full relative bg-[#F6F4F0]`}>
            <div className="flex gap-0 h-full w-full max-w-full">
              {/* Mobile Orders Overlay */}
              {showMobileOrders && (
                <div
                  className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-[100] transition-opacity"
                  onClick={() => setShowMobileOrders(false)}
                >
                  <div
                    className="absolute left-0 top-0 bottom-0 w-[85vw] max-w-[320px] bg-white shadow-2xl overflow-hidden flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between z-10 flex-shrink-0">
                      <h2 className="font-semibold text-gray-900 text-lg">{t('orders.title', { defaultValue: 'Orders' })}</h2>
                      <button
                        onClick={() => setShowMobileOrders(false)}
                        className="p-2 hover:bg-gray-50 rounded-full transition-colors"
                      >
                        <X className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <OrdersPanelStatic
                        newOrderCreated={handle_new_order_created}
                        clearChat={clearChat}
                        onOrdersUpdate={handleOrdersUpdate}
                        hasNewMessagesForOrder={hasNewMessagesForOrder}
                        isMinimized={false}
                        onToggleMinimize={() => { }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Messages Container */}
              {/* No bottom padding here: MessageList reserves the composer's
                  measured height (--composer-h) itself — adding it twice left a
                  ~300px hole on desktop. */}
              <div className="flex-1 min-w-0 overflow-y-auto relative" data-chat-scroll>

                {/* A skipped clarification keeps its line open — say so at the top. */}
                <SkippedLinesBar lines={trackerLines} t={t} />

                {/* Floating parts tracker — single live view of the session's parts list */}
                <PartsTrackerPanel
                  items={trackedParts}
                  serviceTitle={trackedServiceTitle}
                  foundParts={trackerFoundParts}
                  notFoundParts={trackerNotFoundParts}
                  annotatedImages={trackerAnnotatedImages}
                  visionPartImages={trackerVisionPartImages}
                  cartItems={cartItems}
                  isSearching={isLoading}
                  readOnly={isReadOnly}
                  onSendSilent={async (text: string) => {
                    if (!activeSessionId || isLoading) return;
                    await streamSend({
                      message: text,
                      sessionId: activeSessionId,
                      language: currentLanguage,
                      contextTags,
                      filesData: undefined,
                      userMessage: null,
                      onBackendTags: setBackendTags,
                    });
                  }}
                />

                {(isLoadingHistory || isWaitingForSession || (!!urlSessionId && !activeSessionId)) && messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center gap-4">
                      <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-blue-100 border-t-blue-600" />
                      <p className="text-sm text-gray-400">{t('chat.loadingSession', { defaultValue: 'Loading session…' })}</p>
                    </div>
                  </div>
                ) : !activeSessionId && !urlSessionId && messages.length === 0 && !isLoading && !isLoadingHistory && !isWaitingForSession ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center max-w-md p-8">
                      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                        <ShoppingCart className="w-10 h-10 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        {t('chat.emptyState.title', { defaultValue: 'No Active Session' })}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {t('chat.emptyState.message', {
                          defaultValue: 'Select an existing order or create a new one to start chatting.'
                        })}
                      </p>
                    </div>
                  </div>
                ) : (
                  <MessageList
                    // Messages are keyed by position inside the list, so the
                    // whole list is remounted when the conversation changes.
                    key={activeSessionId || 'new'}
                    messages={messages}
                    readOnly={isReadOnly}
                    activeOrderId={getActiveOrderId() || null}
                    hasDraftOrders={hasDraftOrders}
                    isLoading={isLoading}
                    isLoadingHistory={isLoadingHistory}
                    isBackgroundProcessing={isBackgroundProcessing}
                    expandedErrors={expandedErrors}
                    addingToCartItems={addingToCartItems}
                    messagesEndRef={messagesEndRef}
                    errorTooltipRef={errorTooltipRef}
                    onRetry={retryMessage}
                    onFeedback={handleMessageFeedback}
                    onSearchPart={(searchText) => {
                      setInputMessage(searchText);
                      // inputRef.current?.focus();
                    }}
                    // The side chat's "Search for …": a new part request, sent
                    // as the customer's own message on the main page.
                    onSendMessage={(text: string) => {
                      if (!activeSessionId || isLoading || isReadOnly || !text.trim()) return;
                      void sendMessageWithInitialData(text.trim(), []);
                    }}
                    onSendSilent={async (text: string) => {
                      if (!activeSessionId || isLoading || isReadOnly) return;
                      // Strip SYSTEM_RETRY: prefix before forwarding to the backend
                      const cleanText = text.startsWith('SYSTEM_RETRY:') ? text.slice('SYSTEM_RETRY:'.length).trim() : text;
                      await streamSend({
                        message: cleanText,
                        sessionId: activeSessionId,
                        language: currentLanguage,
                        contextTags,
                        filesData: undefined,
                        userMessage: null,
                        onBackendTags: setBackendTags,
                      });
                    }}
                    onSubmitClarification={async (answerMessage, structuredAnswer) => {
                      // Only the order owner answers; a non-owner sees the form locked.
                      if (!activeSessionId || isLoading || isReadOnly) return;
                      // The question is answered the moment it is sent: drop the
                      // attention strip now (server clears the flag at turn start;
                      // the header poll reconciles if the turn re-asks).
                      const cur = getOrderObject();
                      if (cur?.attention_flag === 'needs_you' && (cur.attention_reason === 'questions_internal' || cur.attention_reason === 'cannot_separate' || cur.attention_reason === 'assistance_requested')) {
                        setOrderObject({ ...cur, attention_flag: null, attention_reason: null, attention_details: null, stage: 'analysing' });
                      }
                      // Keep the answered card on screen. Once the answer turn
                      // appends new bot bubbles this question is no longer the
                      // last message, and an unanswered question that is not
                      // last is hidden as superseded — so the picked values
                      // vanished until a reload re-attached the stored answers.
                      // Stamp them locally now, in the shape history uses.
                      const answered: Record<string, unknown> = {};
                      for (const a of ((structuredAnswer as any)?.answers ?? []) as Array<{ question_id?: string }>) {
                        if (a?.question_id) answered[a.question_id] = a;
                      }
                      setMessages(prev => {
                        for (let i = prev.length - 1; i >= 0; i--) {
                          const m = prev[i];
                          if (m.sender !== 'bot' || !(m.text || '').includes('"clarification_questions"')) continue;
                          try {
                            const env = JSON.parse((m.text || '').trim());
                            if (!Array.isArray(env?.clarification_questions) || env.clarification_answers != null) return prev;
                            const next = [...prev];
                            next[i] = { ...m, text: JSON.stringify({ ...env, clarification_answers: answered }) };
                            return next;
                          } catch {
                            return prev;
                          }
                        }
                        return prev;
                      });
                      const userMessage: Message = {
                        id: newMessageId('user'),
                        text: answerMessage,
                        vin: null,
                        datasetId: null,
                        language: currentLanguage,
                        sender: 'user',
                        timestamp: new Date(),
                        // The answered clarification card already shows the
                        // answer — don't echo it as a user bubble too.
                        hiddenFromChat: true,
                      };
                      await streamSend({
                        message: answerMessage,
                        sessionId: activeSessionId,
                        language: currentLanguage,
                        contextTags,
                        filesData: undefined,
                        userMessage,
                        onBackendTags: setBackendTags,
                        structuredAnswer: structuredAnswer as unknown as Record<string, unknown>,
                      });
                    }}
                    onAddToCart={addVisionPartToCart}
                    onToggleErrorDetails={toggleErrorDetails}
                    onCopyErrorDetails={copyErrorDetails}
                    formatTime={formatTime}
                    isValidImageData={isValidImageData}
                    onOrderUpdate={refreshAssociatedOrder}
                    orderUpdateTrigger={orderRefreshTrigger}
                    userPhotoURL={currentUser?.picture}
                    cartItems={cartItems}
                    userDisplayName={currentUser?.displayName}
                    guestHistory={guestHistory}
                    guestHistoryBuyer={guestHistoryBuyer}
                    onSummaryItems={handleSummaryItems}
                    onValidationParts={handleValidationParts}
                    validationItems={trackedParts}
                    complementaryPending={complementaryPending}
                  />
                )}
              </div>

              {/* Cart Side Panel - Always visible on desktop, drawer on mobile */}
              {getOrderObject() && <CartSidePanel
                cartItems={cartItems}
                onMinimizedChange={setCartMinimized}
                onRemovePart={handleRemovePart}
                onUpdateQuantity={handleUpdatePartQuantity}
                onMagicEdit={handleMagicEdit}
                onLaborUpdate={handleLaborUpdate}
                onPriceUpdate={handlePriceUpdate}
                onDeliveryUpdate={handleDeliveryUpdate}
                onDescriptionUpdate={handleDescriptionUpdate}
                onNoteUpdate={handleNoteUpdate}
                onLineDiscountUpdate={handleLineDiscountUpdate}
                onWorkflowLog={handleWorkflowLog}
                onEstimateRepairs={handleEstimateRepairs}
                onRefreshItems={async () => {
                  const orderId = getActiveOrderId();
                  if (!orderId) return;
                  const updated = await orderService.getOrderById(orderId);
                  applyServerOrder(updated);
                }}
                onRecheckInventory={handleRecheckInventory}
                isMobileOpen={showMobileCart}
                onMobileClose={() => setShowMobileCart(false)}
                readOnly={isReadOnly}
              />}
            </div>
          </div>

          {/* Input */}
          {isOrderCompleted ? (
            <div className="border-t border-gray-200 bg-emerald-50 px-4 py-3 flex items-center gap-3 text-sm text-emerald-800">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span className="flex-1">
                {t('orders.completed.banner', { defaultValue: 'This order has been completed and sent to the customer.' })}
              </span>
            </div>
          ) : isOrderCancelled ? (
            <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 flex items-center gap-3 text-sm text-gray-700">
              <X className="w-4 h-4 flex-shrink-0 text-gray-400" />
              <span className="flex-1">
                {t('orders.cancelled.banner', { defaultValue: 'This order was cancelled{{by}}{{reason}}.', by: activeLifecycleOrder?.extra_data?.cancellation?.actor ? ` by ${activeLifecycleOrder.extra_data.cancellation.actor}` : '', reason: activeLifecycleOrder?.extra_data?.cancellation?.reason ? ` — ${activeLifecycleOrder.extra_data.cancellation.reason}` : '' })}
              </span>
            </div>
          ) : isReadOnly ? (
            <div className="border-t border-gray-200 bg-amber-50 px-4 py-3 flex items-center gap-3 text-sm text-amber-800">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span className="flex-1">
                {isSharedView
                  ? t('orders.share.viewOnlyBanner', { defaultValue: 'You are viewing a shared order in read-only mode. You cannot send messages or modify this order.' })
                  : t('orders.org.ownedByBanner', { defaultValue: '{{name}} owns this order. Take it to work on it — that prevents two people working the same order.', name: orderOwnerName || t('orders.org.anotherMember', { defaultValue: 'Another team member' }) })}
              </span>
              {isOrgMemberView && (
                <button
                  onClick={handleTakeOwnership}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#8A5A00] hover:bg-[#704900] text-white text-xs font-semibold transition-colors"
                >
                  {t('orders.owner.takeIt', { defaultValue: 'Take it' })}
                </button>
              )}
            </div>
          ) : (
          <ChatInput
            cartWidthMode={!getOrderObject() ? 'none' : (cartMinimized ? 'collapsed' : 'expanded')}
            inputMessage={inputMessage}
            selectedImagePreview={selectedImagePreview}
            selectedFiles={selectedFiles}
            isLoading={isLoading}
            quotaPaused={Boolean(quotaPausedMetrics['llm_queries'])}
            mapChoiceRequired={!isLoading && hasPendingDiagramChoice(messages)}
            activeSessionId={activeSessionId || null}
            hasDraftOrders={hasDraftOrders}
            hasSubmittedRating={hasSubmittedRating}
            hasMessages={messages.length > 0}
            inputRef={inputRef}
            imageInputRef={imageInputRef}
            fileInputRef={fileInputRef}
            onInputChange={setInputMessage}
            onKeyPress={handleKeyPress}
            onSend={sendMessage}
            onStop={stopStreaming}
            onImageSelect={handleImageSelect}
            onFileSelect={handleFileSelect}
            onClearImage={clearSelectedImage}
            onClearFile={clearSelectedFile}
            onShowMobileOrders={handleShowMobileOrders}
            onShowMobileCart={handleShowMobileCart}
            onShowSessionRating={handleShowSessionRating}
            onResetChat={handleResetChat}
            cartItemsCount={totalItems}
            onRequestAssistance={requestAssistance}
            onAudioSend={(audioFile) => {
              const msgId = newMessageId('user');
              // Mark the file as being transcribed so the UI shows "Transcribing…" immediately
              const fileWithPending = { ...audioFile, isTranscribing: true };
              const userMessage: Message = {
                id: msgId,
                text: '',
                vin: null,
                datasetId: null,
                language: currentLanguage,
                sender: 'user',
                timestamp: new Date(),
                uploadedFiles: [fileWithPending],
              };
              // Stream immediately — don't wait for transcription
              streamSend({
                message: '',
                sessionId: activeSessionId!,
                language: currentLanguage,
                contextTags,
                filesData: [{ data: audioFile.data, mimeType: audioFile.mimeType }],
                userMessage,
                onBackendTags: setBackendTags,
              });
              // Transcribe in the background and patch the message once done
              transcribeAudio(audioFile.data, audioFile.mimeType, activeSessionId ?? undefined, currentLanguage)
                .then((transcript) => {
                  setMessages(prev => prev.map(m =>
                    m.id === msgId
                      ? {
                          ...m,
                          uploadedFiles: m.uploadedFiles?.map(f =>
                            f.mimeType.startsWith('audio/')
                              ? { ...f, transcript: transcript ?? undefined, isTranscribing: false }
                              : f
                          ),
                        }
                      : m
                  ));
                })
                .catch(() => {
                  // Clear the indicator even if transcription failed
                  setMessages(prev => prev.map(m =>
                    m.id === msgId
                      ? {
                          ...m,
                          uploadedFiles: m.uploadedFiles?.map(f =>
                            f.mimeType.startsWith('audio/')
                              ? { ...f, isTranscribing: false }
                              : f
                          ),
                        }
                      : m
                  ));
                });
            }}
            retryState={retryState}
            showRetryPrompt={!isLoading && messages.length > 0 && (
              (messages[messages.length - 1]?.sender === 'user' && !messages[messages.length - 1]?.hasError) ||
              (messages[messages.length - 1]?.hasError === true)
            )}
            lastMessageHasError={messages[messages.length - 1]?.hasError === true}
            errorMessage={messages[messages.length - 1]?.hasError ? messages[messages.length - 1]?.errorDetails?.message : null}
            onRetryLastMessage={() => {
              const lastMsg = messages[messages.length - 1];
              // If last message is an error bot message, find the previous user message
              if (lastMsg?.hasError && lastMsg.sender === 'bot') {
                const lastUserMsg = [...messages].reverse().find(m => m.sender === 'user');
                if (lastUserMsg) retryMessage(lastUserMsg);
              } else if (lastMsg && lastMsg.sender === 'user') {
                retryMessage(lastMsg);
              }
            }}
          />
          )}

          {/* Assistance Request Modal */}
          {activeSessionId && getOrderObject() && (
            <AssistanceRequestModal
              isOpen={showAssistanceModal}
              onClose={() => setShowAssistanceModal(false)}
              orderId={getOrderObject()!.id}
              sessionId={activeSessionId}
              orderName={getOrderObject()!.name || t('orders.orderNumber', { defaultValue: 'Order #{{number}}', number: getOrderObject()!.external_order_id?.split('-').pop() })}
              onSuccess={handleAssistanceSuccess}
            />
          )}
          <ConfirmDialog
            isOpen={takePromptOpen}
            onClose={() => setTakePromptOpen(false)}
            onConfirm={() => { void handleTakeOwnership(); }}
            variant="info"
            title={t('orders.owner.takePromptTitle', { defaultValue: 'Take this order?' })}
            message={t('orders.owner.takePromptBody', { defaultValue: '{{name}} owns this order, so it is read-only for you. Take it to answer questions, edit the cart and manage it.', name: orderOwnerName || t('orders.org.anotherMember', { defaultValue: 'Another team member' }) })}
            confirmText={t('orders.owner.takeIt', { defaultValue: 'Take it' })}
            cancelText={t('common.cancel', { defaultValue: 'Cancel' })}
          />
        </LockedOrderContext.Provider>
      </div>
    </AppLayout>
  );
};
export default ChatPage;
